import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useOutcomeSfx } from "./game-sfx";
import { END_REVEAL_DELAY_MS, useEndReveal } from "./use-end-reveal";
import { useStore } from "zustand";
import { Boot } from "../components/Boot";
import { Button } from "../components/Button";
import { ConfirmDialog } from "../components/ConfirmDialog";
import { SeatInviteSheet } from "../components/SeatInviteSheet";
import { displayName } from "../components/profile-ui";
import {
  DEFAULT_CARD_DECK,
  GAME_CATEGORY,
  isLeaveLockoutMessage,
  isPublicGameInProgressMessage,
  type GameId,
  type TableStatusResponse,
} from "@bing/shared";
import { CardDeckContext } from "./card-decks";
import type { ExitIntent, TableSettings } from "./private-table";
import { useDeckGate } from "./use-card-faces";
import { handKeyOf, useHandDeck } from "./hand-deck";
import { GAME_SERVER_URL } from "../config";
import { fa } from "../i18n/fa";
import {
  clearOnlineGame,
  loadOnlineGame,
  refreshOnlineGameToken,
  saveOnlineGame,
} from "./online-resume";
import { WaitingRoom } from "../screens/WaitingRoom";
import { getBackgammonDifficulty, getBotDifficulty, getMenschDifficulty } from "../settings/prefs";
import { setTableMuted } from "../sound/sfx";
import { useSession, useSessionStore } from "../store/context";
import type { GameResult } from "./connection";
import {
  IdleRemovedNotice,
  KickedNotice,
  LeavePenaltyNotice,
  LeavingNotice,
  LeftToast,
  leftSentence,
  LockoutNotice,
  OpponentAwayOverlay,
  FreeExitNotice,
  ReconnectingToast,
  StartRefusedNotice,
  TopNoticeStack,
  seatName,
  PausedOverlay,
  TableClosedNotice,
  TableExpiredNotice,
  type TableTurnTimer,
} from "./online-lifecycle";
import { seatLeftText, type SeatNotice } from "./seat-notice";
import {
  createOnlineConnector,
  isDefinitiveRejoinFailure,
  type AbsentSeat,
  type ClockInfo,
  type LeavePenaltyInfo,
  type LobbyPlayer,
} from "./online-connection";
import { createOnlineStore } from "./online-store";
import { useTableChannelGate } from "./table-channel-gate";
import { rematchAction } from "./rematch";
import { tableEndLine, tableFateLine } from "./join-error";
import { useOverlayActive } from "./lifecycle";
import { appCrashed, clearAppCrash, type TableOnScreen } from "./crash-return";
import {
  forgetLeftSeat,
  forgetWaitingSeat,
  leaveWaitingSeat,
  refusalWayBack,
  rememberWaitingSeat,
} from "./straight-back";
import { TableChat } from "./TableChat";
import { askHeldSeat, type HeldSeatLookup } from "./held-seat";

/** What the shell hands a game's table renderer. */
export interface OnlineTableArgs<M, V> {
  view: V;
  legal: readonly M[];
  send: (move: M) => void;
  leave: () => void;
  /** The lobby's known seat occupants (name + bot flag), so a table can label real
   * users by name. Undefined until a lobby frame has landed. */
  players?: readonly LobbyPlayer[];
  /** The live turn/pass timer to render ON the felt (play area), or null when there's no
   * active timed turn — threaded so it anchors to the table, not the shell. */
  timer?: TableTurnTimer | null;
  /** CHESS ONLY — the server clock banks for a timed chess table (null for card games + untimed
   * chess). Threaded so the chess table renders + ticks each side's clock in its player strips. */
  clock?: ClockInfo | null;
  /** FEATURE 3 — whether the viewer HOSTS this table (gates the mid-game bot-seat invite). */
  isHost?: boolean;
  /** FEATURE 3 — the host tapped a BOT chair mid-game to invite a friend into it. The table
   *  calls this only for a bot seat while playing; the container opens the friend picker. */
  onInviteSeat?: (seat: number) => void;
  /** The table's own SETTINGS, straight off the lobby frame — the agreed rules of THIS table, as
   *  opposed to whatever the viewer's local pickers happen to say. دوز reads its piece colour
   *  from here so both screens draw the same board; undefined until a lobby frame has landed. */
  settings?: TableSettings | undefined;
  /** RESUMED TABLE — the chairs a persisted game names that nobody is sitting in, with WHOSE they
   *  are. Empty on every ordinary table, and empty again the instant a returning human or the
   *  10-minute resume bot-fill occupies the seat. A table renders it to name the chair honestly
   *  instead of falling through to its solo default. */
  absentSeats?: readonly AbsentSeat[];
  /**
   * AN OPPONENT IS NOT AT THE BOARD RIGHT NOW — their socket dropped and the table is stopped for
   * them. Read off the `paused` frame, which is the only thing that knows: the seat is still listed
   * in `players` as a human (correctly — the chair is still theirs), so no roster reading can see
   * it. Distinct from {@link absentSeats}, which is a chair holding NOBODY on a resumed game.
   *
   * Today exactly one table reads it, and for exactly one reason: شطرنج must stop ticking a clock
   * the server has already frozen. It rides the shared args rather than `ChessOnlineGame`'s own
   * props because تخته نرد pauses through the identical door and will want the same answer the day
   * it grows a clock.
   */
  opponentAway?: boolean;
  /** Leaving RIGHT NOW records nothing — no loss, no leave counter (the same question the exit
   *  dialog asks to pick its wording). Threaded so a table can say it ON THE SCREEN: on a stranded
   *  resume the free exit was one tap behind «خروج», on a screen still insisting you were
   *  mid-game, and pressing it looked like resigning. */
  leaveIsFree?: boolean;
  /**
   * WHAT HAS JUST HAPPENED TO A SEAT, for the CARD tables to say on that seat: a player inside
   * their reconnect grace, and a chair that has changed hands. Empty on every other table (they
   * keep the top pill and the toast) and once the match has a result.
   * Measured, 2026-09-15: `TopNoticeStack`'s slot is the top of the felt on every card table, and
   * there is no spot off the felt that covers nothing. See `seat-notice.tsx`.
   * ⚠️ A card table that does not render these has NO drop or departure notice at all — the shell
   * has stopped drawing both for it. `seat-notice-thread.test.tsx` pins every wrapper's call site.
   * ⚠️ A LIST, because the two co-occur: one seat can be inside its grace while another's
   * departure is still being announced — which is exactly what the stack used to show as two pills.
   */
  seatNotices?: readonly SeatNotice[];
}

/** What the shell hands a game's end-of-match overlay renderer. */
export interface OnlineWinArgs<V> {
  result: GameResult;
  view: V;
  again: () => void;
  /**
   * «بازی دوباره» ON A PRIVATE TABLE IS THE HOST'S CALL (operator, 2026-09-09) — the button
   * appears for the host first and only becomes active for everyone else once the host has
   * pressed it. Every overlay renders these the same way: `disabled` on the button, `hint`
   * as one line under it. Undefined on solo (the overlays are shared) and inert on public,
   * which still re-queues for everyone immediately.
   */
  againDisabled?: boolean;
  againHint?: string;
  /**
   * CONTRACT C3 (session B, 2026-10-08) — the rematch CANNOT work here, so the overlay renders NO
   * «بازی دوباره» button at all; `againHint` (when present) still shows, saying why. Undefined on
   * solo and wherever the button is shown.
   */
  againHidden?: boolean;
  leave: () => void;
  /**
   * The lobby's known seat occupants — the SAME list {@link OnlineTableArgs} carries, so the
   * result card can name the people it just played against.
   *
   * ⚠️ WITHOUT THIS THE END SCREEN CANNOT TELL A PERSON FROM A BOT. Every game's overlay falls
   * back to a positional label, so a table of four real humans announced «ربات ۱ / ۲ / ۳» — the
   * defect reported on prod for بی‌دل on 2026-08-29, and the same shape exists in the پاسور /
   * بلوف / شلم overlays. Undefined until a lobby frame has landed (and on a resumed table before
   * the roster arrives), so every reader must keep its own fallback.
   */
  players?: readonly LobbyPlayer[];
  /**
   * The chairs holding NOBODY on a resumed game, and whose they are — the SAME list
   * {@link OnlineTableArgs} carries (card-98 review E-I3): an end card names an absent human the way
   * the table does, never «ربات» (the 2026-09-04 defect). Undefined until a lobby frame has landed.
   */
  absentSeats?: readonly AbsentSeat[];
}

/**
 * The shared online-game shell for every multi-human game. Owns the connection +
 * store and routes between connecting, the lobby (`WaitingRoom` — quick match OR
 * a host-controlled private table), the live table, and the end-of-match overlay,
 * plus the in-game lifecycle overlays (turn timer + paused). The per-game
 * containers (`HeartsOnlineGame`, …) are thin wrappers that only supply the room
 * name, title, and how to render their own table/win overlay — the private-table
 * lifecycle, host controls, and timers live here once. The server is
 * authoritative; this only renders the redacted view and forwards intent.
 */
export function OnlineGame<M, V>({
  gameId,
  room,
  privateRoom,
  title,
  renderTable,
  renderWin,
  endRevealMs = END_REVEAL_DELAY_MS,
  endRevealHeld = false,
}: {
  /** The game id (`"hearts"`), for the invite deep-link + private-room naming. */
  gameId: string;
  /** The quick-match room name (`"hearts-online"`). */
  room: string;
  /**
   * The HOST/JOIN room name. Defaults to `room` with `-online` → `-private`, which is right for
   * every game whose public queue is a single room type.
   *
   * ⚠️ تخته نرد needs it stated: its public queues are split BY MATCH LENGTH
   * (`backgammon3-online` / `backgammon-online`), and only ONE private room type exists — the
   * derived name would otherwise ask for a `backgammon3-private` that is defined nowhere.
   */
  privateRoom?: string;
  /** The game's Persian title (`fa.hearts.title`) for the waiting-room heading. */
  title: string;
  renderTable: (args: OnlineTableArgs<M, V>) => ReactNode;
  renderWin: (args: OnlineWinArgs<V>) => ReactNode;
  /**
   * How long the end-of-match overlay waits (`useEndReveal`): from the result — or, while
   * `endRevealHeld`, from the moment it turns false. The default is the shell's one beat
   * (`END_REVEAL_DELAY_MS`), which every game but پاسور keeps: پاسور's terminal frame brings its last
   * deal's end — a capture, then the scoop, then the deal's card — so its end card is held behind
   * `endRevealHeld` until its table has shown all of it, then waits only `PASUR_END_MARGIN_MS`. A
   * FUNCTION of the last frame when the beat depends on how the match ended: a match the ROOM ends (a
   * side gone) leaves its frame mid-deal, with no deal end to come, and keeps the shell's beat (P1
   * Task 7). Before any frame: the default.
   */
  endRevealMs?: number | ((view: V) => number);
  /**
   * The table is still showing what its frames brought (پاسور: a moment on screen or waiting — its
   * `onHolding`): the end-of-match overlay waits until it is false, THEN `endRevealMs` (Task 8a,
   * residual 4 — a terminal frame that lands behind a backlog shows the backlog first). Default false:
   * every other game reveals `endRevealMs` after the result, as before.
   */
  endRevealHeld?: boolean;
}): ReactNode {
  const setView = useSession((s) => s.setView);
  // card-a5 item 1 — an in-app way off a PUBLIC table in progress waits for «خروج»'s confirmation
  // (`pendingExit`); the table's own deliberate exits leave without that guard (`leaveTable`); an act
  // elsewhere routed through this table is done once it is left (`runExitIntent`).
  const pendingExit = useSession((s) => s.pendingExit);
  const cancelTableExit = useSession((s) => s.cancelTableExit);
  const leaveTable = useSession((s) => s.leaveTable);
  const runExitIntent = useSession((s) => s.runExitIntent);
  const rejoinOnline = useSession((s) => s.rejoinOnline);
  const returnToHeldSeat = useSession((s) => s.returnToHeldSeat);
  const clearPendingInvite = useSession((s) => s.clearPendingInvite);
  const setActiveGame = useSession((s) => s.setActiveGame);
  const setLiveRoom = useSession((s) => s.setLiveRoom);
  // The crash screen's door reads the table this screen is sitting at (card-a5 item 3).
  const setTableOnScreen = useSession((s) => s.setTableOnScreen);
  const releaseTableOnScreen = useSession((s) => s.releaseTableOnScreen);
  // «بازی دوباره» on a private table re-enters through the ORDINARY invite door, so the successor
  // is an ordinary table in every respect — nothing about a rematch is special on the way in.
  const joinPrivate = useSession((s) => s.joinPrivate);
  // A PUBLIC «بازی دوباره» is a fresh quick-match of this game, whatever door we came in by.
  const playOnline = useSession((s) => s.playOnline);
  const initData = useSession((s) => s.initDataRaw);
  const onlineJoin = useSession((s) => s.onlineJoin);
  const api = useSession((s) => s.api);
  // Our APP id — the id the chat room stamps on every message (`senderId`; it never
  // puts a Telegram id on the wire). Handing the table chat our TELEGRAM id compared
  // two different id spaces, so NOTHING was ever recognised as ours: our own line came
  // back as somebody else's — chiming the received-message sound at us on top of the
  // sent one (both at once) and drawing our own bubbles on the incoming side.
  const selfId = useSession((s) => s.profile?.id ?? "");
  const [round, setRound] = useState(0);
  const [confirmingLeave, setConfirmingLeave] = useState(false);
  /** What a public-table quit cost — held so the notice can be READ before we navigate away — and
   *  where the player was going (the lobby after the table's own «خروج»; elsewhere after an act
   *  elsewhere, card-a5 item 1). */
  const [leavePenalty, setLeavePenalty] = useState<{
    info: LeavePenaltyInfo;
    then: () => void;
  } | null>(null);
  // The live seat's re-join token (roomId + rotating Colyseus token), captured on every connect.
  // We only PERSIST it once the game is "playing" (see the effect below) — a waiting-room / closed /
  // expired table must never linger on the «ادامه بازی» strip.
  const [live, setLive] = useState<{ roomId: string; token: string } | null>(null);
  // RESUMABLE ONLINE GAMES (chess) — the stable `OnlineGame` row this table persists to,
  // announced by the room. Stored with the re-join token so the lobby can recognise the
  // DB-listed copy of this same game instead of showing it twice.
  const [savedGameId, setSavedGameId] = useState<string | null>(null);
  // FEATURE 3 — the bot chair the host tapped to invite a friend into, or null (picker closed).
  const [seatInvite, setSeatInvite] = useState<{ seat: number } | null>(null);

  const { store, onScreen } = useMemo(() => {
    /*
     * THIS STORE'S SEAT, IN MEMORY — what its connector last reported (`onLive`), for the crash
     * screen's door only: never written to the strip's ticket (the save gate keeps a public waiting
     * room off «ادامه بازی»), and one holder PER STORE, so a late landing of an earlier store can never
     * stand in for this table's seat.
     */
    const seat: { current: { roomId: string; token: string } | null } = { current: null };
    const tableStore = createOnlineStore<M, V>(
      createOnlineConnector<M, V>({
        wsUrl: GAME_SERVER_URL,
        room,
        ...(privateRoom ? { privateRoom } : {}),
        getInitData: () => initData,
        join: onlineJoin,
        // Bot-fill strength for a table THIS client creates (quick-match or a host's
        // private table); ignored when joining someone else's table by id.
        //
        // PER GAME, so the Settings row can honestly call itself «دشواری حریف بازی‌های
        // کارتی»: each board game answers with the level the player picked on its own start
        // screen. Chess is pinned MEDIUM server-side, so it ignores whatever is sent; منچ
        // answers with its own TWO-level pref (the server folds the third value away — see
        // `menschLevelFrom`).
        difficulty:
          gameId === "backgammon"
            ? getBackgammonDifficulty()
            : gameId === "mensch"
              ? getMenschDifficulty()
              : getBotDifficulty(),
        // Capture the live seat's token (roomId + rotating token) on connect + every reconnect.
        // The save-when-playing effect below decides when it actually reaches «ادامه بازی».
        //
        // ⚠️ AND WRITE A ROTATION THROUGH AT ONCE. This callback outlives the screen: a reconnect
        // in flight when the overlay's «خروج» unmounted it still lands, and its token is the only
        // one the server will honour from then on — `setLive` on an unmounted screen keeps
        // nothing, so «ادامه بازی» used to ask with the spent one (see `refreshOnlineGameToken`).
        onLive: ({ roomId, token }) => {
          seat.current = { roomId, token };
          setLive({ roomId, token });
          refreshOnlineGameToken(roomId, token);
          if (onlineJoin.kind === "quick") forgetLeftSeat(); // card-98 3b: admitted ⇒ nothing is held
        },
        onSavedGame: (id) => setSavedGameId(id),
        // A re-join whose saved token was refused (match over / gone) → drop the stale entry —
        // THAT room's only: the crash screen's door re-joins a seat the strip never held (a public
        // waiting room), and its failure must not wipe an older table's ticket.
        //
        // ⚠️ THE APP'S OWN STRAIGHT-BACK RETURN (card-a5 item 1) KEEPS THE TICKET UNLESS THE SEAT IS
        // PROVABLY GONE. It runs on every app open, offline included, and a network failure there
        // said nothing about the seat: deleting the only key to it would leave a player whose seat
        // is still held with no way back. A waiting-room seat is forgotten either way — nothing is
        // at stake before the deal.
        onRejoinFailed: (error) => {
          const rejoin = onlineJoin.kind === "rejoin" ? onlineJoin : null;
          if (rejoin?.auto === "waiting") {
            forgetWaitingSeat(rejoin.roomId);
            return;
          }
          // …and so does a return carrying an act elsewhere (`then`): its own way back.
          if (
            (rejoin?.auto === "table" || rejoin?.then !== undefined) &&
            !isDefinitiveRejoinFailure(error)
          )
            return;
          clearOnlineGame(rejoin?.roomId);
          if (isDefinitiveRejoinFailure(error)) forgetLeftSeat(rejoin?.roomId); // card-98 3b: gone for good
        },
        // THE KEY IN HER POCKET. An invite link to a table that has dealt is refused («بازی این
        // میز شروع شده است») — correct for a stranger, wrong for somebody already seated there
        // whose app still holds this device's ticket. Hand the connector that ticket so it can
        // try the door the refusal does not open. Only ever for the SAME room: one saved game is
        // kept, and a token for another table is not a key.
        ticketFor: (id: string) => {
          const saved = loadOnlineGame();
          return saved && saved.roomId === id ? saved.token : null;
        },
        // A «ادامه بازی» return asks the table's fate on its first «not yet», so a table that is
        // OVER says so at once instead of after twelve seconds (session B, item 3).
        ...(onlineJoin.kind === "rejoin"
          ? { tableStatus: (id: string) => api.getTableStatus(id) }
          : {}),
      }),
      // A join-by-id (an invite / a deep link) can fail on a table that no longer takes
      // players — and only when we know WHICH room can we look up what became of it and say so.
      //
      // ⚠️ A RE-JOIN NEEDS THIS AT LEAST AS MUCH, since 2026-08-31. The «ادامه بازی» strip now
      // also offers a PRE-START private table, and that table has a ten-minute idle window — so
      // the entry a host taps is likelier to be dead than a mid-game one, not less. Without the
      // lookup they got the generic connection error about a table whose exact fate the server
      // had recorded and was ready to hand over («این میز منقضی شد» / «میزبان این میز را بست»).
      //
      // `deferConnect`: this runs DURING RENDER, and a render that never commits runs no effect —
      // a socket opened here would have nobody to close it (card-a5 item 3). The committed mount
      // effect below connects.
      {
        deferConnect: true,
        ...(onlineJoin.kind === "join" || onlineJoin.kind === "rejoin"
          ? {
              roomId: onlineJoin.roomId,
              tableStatus: (id: string) => api.getTableStatus(id),
              // …and a RETURN to a finished table also says how it ended for them.
              returning: onlineJoin.kind === "rejoin",
            }
          : {}),
      },
    );
    /** THE TABLE THIS SCREEN IS SITTING AT, for the crash screen's door (`game/crash-return.ts`). */
    const onScreen = (): TableOnScreen => {
      const now = tableStore.getState();
      const humans = (now.lobby?.players ?? []).filter((p) => !p.isBot).length;
      return {
        game: gameId,
        roomId:
          seat.current?.roomId ??
          (onlineJoin.kind === "join" || onlineJoin.kind === "rejoin" ? onlineJoin.roomId : null),
        token: seat.current?.token ?? (onlineJoin.kind === "rejoin" ? onlineJoin.token : null),
        dealt: now.phase === "playing" || now.phase === "gameover",
        // The one seat the game-server does NOT hold after a drop: a PUBLIC waiting room this player
        // sat in alone (`onLeave` → `leaveLobby`, the room reaped at the drop).
        seatHeld: !(now.lobby?.isPrivate === false && now.phase === "waiting" && humans <= 1),
      };
    };
    return { store: tableStore, onScreen };
    // `round` bumps to re-queue a fresh match (new connection) on "play again".
  }, [initData, round, room, privateRoom, onlineJoin, api]);

  /*
   * LEAVING THIS SCREEN CLOSES ITS TABLE — CONSENTED, unless the app has CRASHED (card-a5 item 3).
   *
   * Every deliberate exit closes the table itself before it navigates (`doLeave`, `playSolo`, «خروج
   * بدون بازگشت», the connecting screen's drop), so this cleanup is what closes a table nobody closed
   * — and one of those is a crash: the root `ErrorBoundary` swaps the whole tree for its fallback and
   * this screen unmounts with it. A consented close is what the game-server reads as the player
   * LEAVING (the chair to a bot at once, a private waiting room closed by its host), so a crash closes
   * the way a dead socket closes instead: `drop()`, non-consented — the drop grace, the reservation,
   * nothing charged — and the crash screen offers the way back (`game/crash-return.ts`).
   *
   * The mount half — COMMITTED, so it is where the table connects (never during render: see
   * `deferConnect` above) — lowers a stale latch (a table that mounts is a fresh start, see
   * `clearAppCrash`) and puts this table «on screen» for the crash screen's door. A crash KEEPS it
   * there (the door reads it, and reads it again if the crash recurs before the next table commits);
   * every other unmount releases it.
   */
  useEffect(() => {
    clearAppCrash();
    setTableOnScreen(onScreen);
    store.getState().connect();
    return () => {
      if (appCrashed()) {
        store.getState().drop();
        return;
      }
      releaseTableOnScreen(onScreen);
      store.getState().disconnect();
    };
  }, [store, onScreen, setTableOnScreen, releaseTableOnScreen]);

  // WHICH TABLE ARE WE ACTUALLY AT? Publish the CONNECTED roomId to the session store, because
  // the join INTENT cannot answer that: a quick-matcher's is `{kind:"quick"}` and the matchmaker
  // picks the room. The invite guards (`promptNextIncomingInvite` / `openInvitePrompt`) read it
  // to avoid prompting for the table we are already sitting at — accepting such an invite runs a
  // SECOND join that tears down this connection. Cleared on unmount so the lobby never looks
  // "at a table".
  useEffect(() => {
    if (live) setLiveRoom(live.roomId);
  }, [live, setLiveRoom]);
  useEffect(() => () => setLiveRoom(null), [setLiveRoom]);

  const phase = useStore(store, (s) => s.phase);
  const seat = useStore(store, (s) => s.seat);
  const isHost = useStore(store, (s) => s.isHost);
  const lobby = useStore(store, (s) => s.lobby);
  const settings = useStore(store, (s) => s.settings);
  // Owner-set table policies (default permissive until the first lobby frame): a MUTED
  // table silences all sound for everyone seated here; chat OFF hides the chat surface;
  // the deck is the card-back skin every seated player renders.
  const muted = settings?.muted ?? false;
  const chatAllowed = settings?.chatAllowed ?? true;
  const deck = settings?.deck ?? DEFAULT_CARD_DECK;
  // A card table must never be revealed with nothing to draw — blank cards mid-hand is the one
  // outcome worse than a few more seconds on the loading screen. The gate (`useDeckGate`) starts
  // the host's deck downloading in the background — again when `settings.deck` lands a frame after
  // the join — and lets the felt in on whichever is drawable first: that deck, or «استاندارد»
  // (its own chunk, `game/card-faces.ts`) until the deck is in. It never holds a seated player at a
  // loading screen for the deck itself while the turn clock runs. Board games need nothing. The
  // hook runs here, above every early return.
  const needsFaces = GAME_CATEGORY[gameId as GameId] === "card";
  const faces = useDeckGate(deck, needsFaces);
  const view = useStore(store, (s) => s.view);
  // Once the felt is up it stays up: a face that breaks later (its deck back to «استاندارد»
  // while the classic chunk had failed) leaves rank-and-suit faces on the table — never the
  // «preparing cards» screen over a hand in progress (the batch review's M6).
  const feltShown = useRef(false);
  // The deck the felt DRAWS: latched per hand from the felt's first card (`hand-deck.ts`; no hand
  // while the waiting room or the artwork gate is up, N1) — the host's deck, if it is here at the
  // deal, lands at the next deal, never mid-hand; a deck change mid-hand waits too.
  const feltUp = phase !== "waiting" && (!needsFaces || faces.ready || feltShown.current);
  const drawDeck = useHandDeck(deck, feltUp ? handKeyOf(gameId, view) : null);
  const legal = useStore(store, (s) => s.legal);
  const turn = useStore(store, (s) => s.turn);
  // CHESS ONLY — the server clock banks for a timed table (null for card games / untimed chess).
  const clock = useStore(store, (s) => s.clock);
  const paused = useStore(store, (s) => s.paused);
  // CHESS ONLY — the opponent-away lifecycle (Phase 3c): the reconnect-grace countdown and the
  // claim prompt. Card games never populate these (they use `paused` + PausedOverlay), so this
  // whole path is inert for them.
  const reconnecting = useStore(store, (s) => s.reconnecting);
  const claimable = useStore(store, (s) => s.claimable);
  const leftNotice = useStore(store, (s) => s.leftNotice);
  const freeExitNotice = useStore(store, (s) => s.freeExitNotice);
  const removedForIdle = useStore(store, (s) => s.removedForIdle);
  const acknowledgeRemoval = useStore(store, (s) => s.acknowledgeRemoval);
  const kickedOut = useStore(store, (s) => s.kickedOut);
  const takenOver = useStore(store, (s) => s.takenOver);
  const acknowledgeKick = useStore(store, (s) => s.acknowledgeKick);
  const kick = useStore(store, (s) => s.kick);
  // «بازی دوباره» — the successor the private host has opened (null until they do).
  const rematchRoomId = useStore(store, (s) => s.rematchRoomId);
  const requestRematch = useStore(store, (s) => s.requestRematch);
  // …and whether one can work at all: the server's verdict (C1; null from an old game-server),
  // our own socket to the finished room, and who has left since (the crown is the live `isHost`).
  const rematchState = useStore(store, (s) => s.rematchState);
  const roomGone = useStore(store, (s) => s.roomGone);
  const endDepartures = useStore(store, (s) => s.endDepartures);
  // The server's reason for refusing our «شروع» / «بازی دوباره» (maintenance), for a few seconds.
  const startRefused = useStore(store, (s) => s.startRefused);
  const closedBy = useStore(store, (s) => s.closedBy);
  const closedMidGame = useStore(store, (s) => s.closedMidGame);
  const expiredByIdle = useStore(store, (s) => s.expiredByIdle);
  const result = useStore(store, (s) => s.result);
  const endLobby = useStore(store, (s) => s.endLobby);
  /*
   * WHO THE SCREEN NAMES — the roster the match ENDED with, for as long as a result is shown.
   *
   * ⚠️ NOT THE LIVE ROSTER (session B, 2026-10-08). After the terminal the server still bots the
   * chair of everyone who closes their result card, so the live list renamed them «بات N» on the
   * card, on the felt behind it and in the chat roster. The store freezes the list on the first
   * `gameover`; those three read it. Presence — who is still AT the finished table — is a different
   * question, and the rematch keeps reading the live `lobby` for it.
   */
  const namedPlayers = result !== null ? (endLobby ?? lobby)?.players : lobby?.players;
  // PERSIST the re-join seat (re-saved on each reconnect via the rotating token) while the game is
  // PLAYING — and, since 2026-08-31, while a PRIVATE table is still pre-start.
  //
  // ⚠️ THE OLD `phase === "playing"` GATE WAS THE SECOND HALF OF A REPORTED BUG. A host who caught
  // the mini app's ✕ in their own waiting room had no route back to it at all: the ticket was never
  // written, and their own invite link is refused for the whole thirty-minute hold (the room
  // auto-locked at capacity and colyseus's `unlock()` sits behind the `await` that hold is running,
  // so `joinById` dies on `room.locked` before `onAuth`). The ticket opens that door —
  // `client.reconnect(token)` skips the lock AND `maxClients` — and it was being thrown away.
  //
  // The gate's own reason survives, and is why this is narrow: a saved table must never linger on
  // the strip as a phantom «در جریان». So (a) only a PRIVATE table qualifies, because only its seat
  // is held long enough (thirty minutes; a public table's is 120 s and a public waiting room whose
  // only human drops is reaped AT the drop), (b) the entry is MARKED `pending` so the strip says
  // what it is rather than borrowing a dealt game's wording, and (c) the closed/expired effects
  // below still drop it the moment the room is gone.
  //
  // ⚠️ AND NO TICKET AT ALL ONCE THE SEAT IS NOT THIS COPY'S (card-74 round 2, #2). A kick and an idle
  // removal release the connection but keep the socket, and their screens CLEAR the ticket — so the
  // next frame (the kick's `abandonToBot`/`leaveLobby` broadcast, the seat's state frame) used to write
  // it straight back, through the save below or review #2's put-back: a `pending` / `public` ticket to
  // a table the player is barred from, which the strip and the next open's straight-back then tried for
  // twelve seconds before «فرصت برگشتن…». A copy a newer one took over writes nothing either: the ticket
  // on this phone is the newer copy's. STICKY for this screen's life: the notice's «باشه» clears the
  // flag a render before the screen is gone, and that render must not write the ticket back either.
  const seatLostNow = kickedOut || removedForIdle !== null || takenOver;
  const [seatLostOnce, setSeatLostOnce] = useState(false);
  if (seatLostNow && !seatLostOnce) setSeatLostOnce(true); // React's «adjust state while rendering»
  const seatLost = seatLostNow || seatLostOnce;
  const ticket = useMemo(() => {
    const pending = phase === "waiting" && lobby?.isPrivate === true;
    if (seatLost || !live || (phase !== "playing" && !pending)) return null;
    return {
      roomId: live.roomId,
      game: gameId as GameId,
      token: live.token,
      ...(savedGameId ? { onlineGameId: savedGameId } : {}),
      ...(pending ? { pending: true } : {}),
      // A PUBLIC table in progress — the one ticket the app goes STRAIGHT BACK to on open while the
      // seat is held (card-a5 item 1, `straight-back.ts`). A private table's never says so.
      ...(lobby?.isPrivate === false ? { public: true } : {}),
    };
  }, [seatLost, live, phase, gameId, savedGameId, lobby?.isPrivate]);
  useEffect(() => {
    if (ticket) saveOnlineGame(ticket);
  }, [ticket]);
  /*
   * …AND PUT IT BACK WHEN SOMETHING ON THIS DEVICE TOOK IT AWAY (card-74, review #2). On a phone two
   * copies of the app share this storage, and the copy a takeover let go can delete this table's ticket
   * (its refused «ادامه بازی» return drops it) — leaving this copy, should its page die, with no way back.
   * So every state or lobby frame re-reads the ticket and writes only when it is MISSING or names THIS
   * table differently (another token). Another table's ticket is left alone: that table claimed the one
   * slot when it dealt, and two live tables re-claiming it frame by frame would only trade it back and
   * forth. No timer, so keying on the frame objects cannot strand one (the CLAUDE.md effect gotcha).
   */
  useEffect(() => {
    if (!ticket) return;
    const saved = loadOnlineGame();
    if (saved !== null && saved.roomId !== ticket.roomId) return;
    if (
      saved !== null &&
      saved.game === ticket.game &&
      saved.token === ticket.token &&
      saved.onlineGameId === ticket.onlineGameId &&
      saved.pending === ticket.pending &&
      saved.public === ticket.public
    ) {
      return;
    }
    saveOnlineGame(ticket);
  }, [ticket, view, lobby]);
  /*
   * A PUBLIC WAITING ROOM'S SEAT, FOR THE STRAIGHT-BACK RETURN (card-a5 item 1, the controller's
   * addendum). The server holds it 120 s after a drop (two humans or more), and a crash or a reload
   * in that window used to land in the lobby — where a quick-match of this game is refused («you are
   * already in a public game») for the rest of the 120 s. Re-stamped on EVERY lobby frame, which is
   * why this is keyed on the frame object: the stamp is «the last moment this seat was seen alive»,
   * and the straight back is offered only inside the hold measured from it. No timer here, so the
   * frame-object dependency cannot strand one (the CLAUDE.md effect gotcha is about timeouts).
   * Kept under its own key, never on the «ادامه بازی» strip — a waiting room is not a game.
   */
  const publicWaiting = phase === "waiting" && lobby?.isPrivate === false;
  useEffect(() => {
    if (!live || !publicWaiting) return;
    rememberWaitingSeat({ roomId: live.roomId, game: gameId as GameId, token: live.token });
  }, [live, publicWaiting, lobby, gameId]);
  // …and FORGOTTEN the moment it stops being a waiting-room seat: the table dealt (the ticket above
  // takes over), ended, or refused us.
  useEffect(() => {
    if (live && phase !== "waiting" && phase !== "connecting") forgetWaitingSeat(live.roomId);
  }, [live, phase]);
  // …and when this screen goes away IN-APP: a waiting room the player walked out of is not a seat to
  // drag them back to on the next open. A crash keeps it — the crash screen's reload goes straight
  // back (a crash is a lost connection, never a departure; nothing here charges anything either way).
  const liveRef = useRef(live);
  liveRef.current = live;
  /** A «خروج» is in flight (see `doLeaveForever`). */
  const leavingForever = useRef(false);
  const [leaving, setLeaving] = useState(false); // card-98 2.1 — the same, for the screen
  useEffect(
    () => () => {
      if (appCrashed()) return;
      const left = liveRef.current;
      if (left) leaveWaitingSeat(left.roomId); // card-98 item 3b: kept only for the refusal's way back
    },
    [store],
  );
  // The match ended → this game is no longer re-joinable; drop the saved seat so «ادامه بازی» stops
  // offering it. (A «خروج» does NOT clear it — an online leave stays reclaimable during the match.)
  useEffect(() => {
    if (result !== null) clearOnlineGame();
  }, [result]);
  // The OWNER muted this table → suppress EVERY sound (game + chat) for every seated
  // player, the whole time they're on this table. Reset on leave so the lobby / next
  // game isn't left silently muted (the shell owns this transient, never-persisted flag).
  useEffect(() => {
    setTableMuted(muted);
    return () => setTableMuted(false);
  }, [muted]);
  // A CLOSED (owner canceled the table) or idle-EXPIRED table is DESTROYED — the room is gone, never
  // reclaimable. Drop the saved game now so it can't linger on the «ادامه بازی» strip.
  useEffect(() => {
    if (closedBy !== null || expiredByIdle) {
      clearOnlineGame();
      forgetWaitingSeat();
    }
  }, [closedBy, expiredByIdle]);
  // The online summary used to POP the instant the terminal frame landed — the
  // final trick never got to sweep. Same held beat as the solo containers, and
  // the win/lose chime now lands WITH the overlay (it used to fire ~1.4s early). The beat is the
  // game's own where its last move's animation is longer (`endRevealMs`: پاسور's deal end).
  const revealMs =
    typeof endRevealMs === "function"
      ? view === null
        ? END_REVEAL_DELAY_MS
        : endRevealMs(view)
      : endRevealMs;
  const showEnd = useEndReveal(result !== null && !endRevealHeld, revealMs);
  useOutcomeSfx(showEnd, result?.winners, seat);
  const seatTokenMinted = useStore(store, (s) => s.seatTokenMinted);
  const error = useStore(store, (s) => s.error);
  // A «ادامه بازی» return to a FINISHED table: how it ended, when the server recorded it.
  const tableEnd = useStore(store, (s) => s.tableEnd);
  // The app-root reconnect overlay owns the screen while it's up — don't ALSO
  // paint this container's raw-error screen behind it (that leaked English).
  const overlayActive = useOverlayActive();
  /*
   * THE APP'S OWN STRAIGHT-BACK RETURN FAILED (card-a5 item 1) — SILENTLY. Nobody asked for this table:
   * the app came here by itself on open because the device held a seat at it. If the seat is not there
   * any more (the match ended while they were away, the room is gone, the waiting room dealt without
   * them) or the network is down, the app simply opens where it would have opened — never an error
   * screen about a game the player did not tap. (`onRejoinFailed` above decides whether the ticket is
   * kept.)
   */
  const autoReturnFailed =
    onlineJoin.kind === "rejoin" && onlineJoin.auto !== undefined && phase === "error";
  useEffect(() => {
    if (autoReturnFailed) setView("lobby");
  }, [autoReturnFailed, setView]);
  /** An act elsewhere routed through this table, which turned out not to be reachable (card-a5 item 1):
   *  nothing to leave, so the act simply happens — no error screen either (the effect further down). */
  const routedReturnFailed =
    onlineJoin.kind === "rejoin" && onlineJoin.then !== undefined && phase === "error";
  // THE GAME-SERVER REFUSED THE JOIN FOR CHANNEL MEMBERSHIP — the channel-join screen, whose «عضو
  // شدم» re-attempts this same join (a fresh store: `round`). See `useTableChannelGate`.
  const channelGate = useTableChannelGate({
    status: phase,
    error,
    restart: () => setRound((r) => r + 1),
    // Out of a refusal, the way the error screen's «باشه» goes (the unmount closes the store); out of a
    // retry still connecting, the way the connecting screen's exit goes — a DROP, never consented.
    onLeave: () => {
      if (store.getState().phase === "connecting") store.getState().drop();
      setView("lobby");
    },
  });
  const send = useStore(store, (s) => s.send);

  // When MY table STARTS (waiting → playing), withdraw any still-pending invites I sent to THIS
  // room — the friend missed the window, so their incoming-invite counter/prompt clears live (the
  // `invite_canceled` push → `dropIncomingInvite`), instead of them tapping into a locked table.
  // Read the invites imperatively (fresh) so this doesn't subscribe the container to the list.
  const sessionStore = useSessionStore();
  /*
   * …AND A PRIVATE WAITING ROOM WALKED OUT OF IN-APP TAKES ITS «ادامه بازی» CARD WITH IT (card-86
   * round 3, item 2 — the operator: «A host's lobby keeps showing a private table they just left …
   * remove it right away»). The `pending` ticket is the way back for a host whose APP closed — a
   * drop, whose seat the server holds thirty minutes. A «خروج» the server HEARD is different: the
   * seat is freed at once and a host's whole table closed (`leaveLobby`), so the card «میز خصوصی ·
   * هنوز شروع نشده» could only fail. Cleared ONLY on the proof — the game-server's CONSENTED close of
   * THIS room after our `leave()` (`leaveHeard`, one round trip after the lobby shows), and the strip
   * re-reads at once. Never on another close code, never when none comes: on a dead or silently dead
   * socket nothing reached the server, which holds the seat (a host's table, guests and all) as a
   * drop, and this ticket is the one way back in. Kept too after a crash and by a copy a newer one
   * took over (the ticket is that copy's); a public waiting room keeps none. Watched from the store,
   * which outlives this screen.
   *
   * ⚠️ …AND A «خروج» THAT RACED THE DEAL KEEPS IT, AS A DEALT TABLE'S (NICE 1). The server dealt
   * before it heard us, so the seat is held reclaimable (`onLeave`'s mid-game door). The deal's
   * frames leave the server ahead of its close, so a state frame landing on the old socket first says
   * exactly that: the ticket stays, without `pending`, whatever close follows.
   */
  useEffect(
    () => () => {
      if (appCrashed()) return;
      const left = liveRef.current;
      const now = store.getState();
      if (!left || now.phase !== "waiting" || now.takenOver) return;
      if (loadOnlineGame()?.roomId !== left.roomId) return; // the slot names another table
      const refreshStrip = (): void =>
        sessionStore.setState((st) => ({ resumeStripVersion: st.resumeStripVersion + 1 }));
      const unsubscribe = store.subscribe((s) => {
        if (s.phase === "playing") {
          unsubscribe();
          const saved = loadOnlineGame();
          if (saved?.roomId !== left.roomId || saved.pending !== true) return;
          saveOnlineGame({
            roomId: saved.roomId,
            game: saved.game,
            token: saved.token,
            ...(saved.onlineGameId !== undefined ? { onlineGameId: saved.onlineGameId } : {}),
          });
          refreshStrip();
          return;
        }
        if (s.leaveHeard !== left.roomId) return;
        unsubscribe();
        clearOnlineGame(left.roomId);
        refreshStrip();
      });
    },
    [store, sessionStore],
  );
  const prevPhaseRef = useRef(phase);
  const roomId = lobby?.roomId;
  // MY one live invite to THIS table, for the waiting room's pending line. Subscribed (unlike
  // `withdrawMyInvites`, which reads fresh) because the UI has to re-render when it settles:
  // the friend accepting/declining, or the withdraw above, must put the «+» badges back.
  const outgoingInvites = useSession((s) => s.outgoingInvites);
  const myTableInvite = useMemo(() => {
    if (!roomId) return null;
    const live = outgoingInvites.find((i) => i.roomId === roomId && i.status === "PENDING");
    return live ? { id: live.id, name: displayName(live.to) } : null;
  }, [outgoingInvites, roomId]);

  /** Withdraw every still-PENDING invite I sent to THIS table (read fresh, not subscribed). */
  const withdrawMyInvites = useCallback((): void => {
    if (!roomId) return;
    const s = sessionStore.getState();
    for (const inv of s.outgoingInvites) {
      if (inv.roomId === roomId && inv.status === "PENDING") {
        void s.cancelGameInvite(inv.id).catch(() => {});
      }
    }
  }, [roomId, sessionStore]);
  useEffect(() => {
    const prev = prevPhaseRef.current;
    prevPhaseRef.current = phase;
    const s = sessionStore.getState();
    // A) The invited game (accept-invite switch) finished CONNECTING → it now has a real phase to
    // show (waiting / انتخاب یار و حریف / the table / an error) → drop the switch transition overlay
    // so the friend lands on that real stage, never the lobby. The store starts every online game at
    // "connecting", so only the freshly-joined game trips this — a game already playing never does.
    if (prev === "connecting" && phase !== "connecting") s.clearGameSwitch();
    // B) MY table STARTED (waiting → playing) → withdraw any still-pending invites I sent to THIS
    // room, so the friend's incoming-invite counter/prompt clears live (via the invite_canceled push)
    // instead of them tapping into a locked table.
    if (prev === "waiting" && phase === "playing" && roomId) {
      withdrawMyInvites();
    }
  }, [phase, roomId, sessionStore, withdrawMyInvites]);

  // THE TABLE FILLED — withdraw before the deal, not at it.
  //
  // A full house does NOT deal immediately: it runs the 10s «بازیکن‌ها تکمیل شدند» beat (منچ opens
  // its colour stage on the same countdown), and the room auto-LOCKS at maxClients the moment the
  // last seat is taken. So between "full" and "playing" there is a window in which the friend still
  // holds a live prompt into a table that can no longer be joined. Withdraw on FULL; the
  // waiting → playing rule above stays as the backstop for a bot-filled deal.
  //
  // Pre-start a full roster is genuinely four HUMANS: bots only enter it at `controller.start()`,
  // which flips the phase to playing first.
  const rosterFull = lobby !== null && lobby.players.length >= lobby.seats;
  useEffect(() => {
    if (rosterFull && phase === "waiting") withdrawMyInvites();
  }, [rosterFull, phase, withdrawMyInvites]);

  // I LEFT THE TABLE — withdraw every invite I sent to it, in EVERY case.
  //
  // «خروج» / «خروج بدون بازگشت» / accepting another invite / any navigation all unmount this
  // container, and the friend must not be left holding a prompt into a table whose host is gone —
  // a lone host leaving takes the table with them, and even a surviving table is no longer the one
  // they were invited to. Deliberately unconditional: a friend arriving in the same instant does
  // not save the invite (the host is not there any more); if the host rejoins, the dead invite
  // stays dead and they invite again.
  //
  // Read through a REF so the cleanup withdraws with the LATEST roomId, and runs on unmount ONLY —
  // keying the effect on the callback would fire it every time `roomId` changed (e.g. the first
  // lobby frame), withdrawing against a room we had not been in.
  const withdrawRef = useRef(withdrawMyInvites);
  withdrawRef.current = withdrawMyInvites;
  useEffect(() => () => withdrawRef.current(), []);

  // FEATURE 3 — the server minted a seat-fill reconnection token for the host's pick: create the
  // friend-invite carrying it (seat + token), then clear it. The friend accepts → reconnects in.
  useEffect(() => {
    if (!seatTokenMinted) return;
    const { seat: filledSeat, toUserId, roomId: r, token, game } = seatTokenMinted;
    void sessionStore
      .getState()
      .sendGameInvite(toUserId, (game ?? gameId) as GameId, r, {
        seat: filledSeat,
        reconnectToken: token,
      })
      .catch(() => {});
    store.getState().clearSeatTokenMinted();
    setSeatInvite(null);
  }, [seatTokenMinted, gameId, store, sessionStore]);

  /**
   * «بازی تک‌نفره» — the lone player's way off an open-ended wait.
   *
   * A public table now counts nothing until a SECOND human sits down, so «منتظر بازیکن واقعی» has
   * no deadline: the screen must therefore offer the alternative rather than leave someone staring
   * at a stopped clock. Leaves the table (freeing the seat) and opens the SOLO game, which has its
   * own difficulty settings — the honest version of what an auto-bot-filled public table was
   * silently giving them.
   *
   * No confirm dialog: nothing is at stake pre-start (that is why the waiting room's own exit is a
   * single «خروج» too), and the invite this may withdraw is covered by the unmount rule above.
   */
  const playSolo = (): void => {
    store.getState().disconnect();
    leaveWaitingSeat(); // walked out of the waiting room — kept only for the refusal (card-98 3b)
    setActiveGame(gameId);
  };

  const doLeave = (): void => {
    store.getState().disconnect();
    leaveWaitingSeat(); // a deliberate exit — kept only for the refusal's way back (card-98 3b)
    // Past every confirmation it owes (or owing none: a kick, an idle removal, a closed table, the
    // result screen) — so it leaves without the public-table guard (card-a5 item 1).
    leaveTable("lobby");
  };
  // «خروج بدون بازگشت» — a PERMANENT leave: tell the server this exit is final (no reclaimable seat
  // held), drop the saved game so «ادامه بازی» never offers it, and go to the lobby — or, since card-a5
  // item 1, wherever the player was going when they confirmed it (`after`): a toast's screen, the
  // lobby the reconnect overlay offered, or the game they were starting from the lobby. You can't return.
  //
  // ⚠️ THE SAVED SEAT IS DROPPED ONLY ONCE THE SERVER HAS ANSWERED (card-a5 item 1, review fix). The
  // server answers EVERY `leave-forever` it hears (`applyPublicLeavePenalty`, «nothing recorded»
  // included), so a verdict is the proof that the seat was given up. No verdict — a half-open socket
  // that swallowed the frame (the frozen table a player presses «خروج» on), a socket that never came
  // back (`LeaveNotDeliveredError`), or an app killed while it waited — and the server may still hold
  // the seat and refuse every other public game: this device's key is then its ONLY way back, so it
  // stays. A key whose seat did go is shed by itself on the next return (`isDefinitiveRejoinFailure`).
  //
  // ONE «خروج» AT A TIME (review fix): a second confirm while the first still waits for its socket or
  // its verdict — the reconnect overlay comes back over a leave in flight — sends nothing more: a
  // second call would take over the verdict's listener, and the first's empty answer would then walk
  // the player away from the second's notice. One leave, one verdict, one notice.
  const doLeaveForever = (after: () => void = () => leaveTable("lobby")): void => {
    if (leavingForever.current) return;
    leavingForever.current = true;
    setLeaving(true); // card-98 2.1 — شطرنج's «در حال خروج…» at once; the waits below are unchanged
    const roomId = liveRef.current?.roomId;
    void store
      .getState()
      .leaveForever()
      .then((info) => {
        if (info !== null) clearOnlineGame(roomId); // the server heard «خروج»: the seat is given up
        // A PUBLIC table in progress answers with what the leave cost; hold the notice until they
        // acknowledge it (the count is the part that changes behaviour). Everything else — a
        // private table, the win screen, an unanswered server — goes straight on, exactly as before.
        if (info?.recordedLoss) setLeavePenalty({ info, then: after });
        else after();
      })
      // «خروج» NEVER REACHED THE SERVER: nothing was recorded, the seat is still held and the key to
      // it is still saved — go where they asked, for free.
      .catch(() => after())
      .finally(() => {
        leavingForever.current = false;
        setLeaving(false);
      });
  };
  // Live in-game exits are gated behind the shared confirm dialog. MID-GAME the online seat is
  // reclaimable (see the re-join feature), so the dialog offers TWO choices — «خروج» (jات به ربات،
  // can return via «ادامه بازی») vs «خروج بدون بازگشت» (permanent) — mirroring the offline keep/end
  // dialog. PRE-START (waiting room) there's nothing to reclaim, so it stays the single «خروج».
  // The WIN overlay's exit is a plain `doLeave` (the match is over — nothing to protect).
  const leave = (): void => setConfirmingLeave(true);
  /**
   * A PUBLIC table has ONE exit and taking it mid-game costs a recorded loss (2026-08-27), so its
   * dialog is single-action and says so BEFORE they take it. A PRIVATE table keeps the two-option
   * «خروج» / «خروج بدون بازگشت» dialog: nothing is at stake there.
   */
  // A copy that was let go (card-74) has no game in progress any more: its table is the other copy's.
  const publicMidGame = lobby?.isPrivate === false && phase === "playing" && !takenOver;
  /**
   * NOBODY REAL TO ABANDON (widened 2026-08-29) — the server records nothing for this exit, so the
   * dialog must stop promising a loss.
   *
   * ⚠️ THE SERVER IS THE AUTHORITY; this only picks the sentence. `TableController.leaveIsFree`
   * asks the same two questions of the engine at the moment the exit is taken, so a table that
   * changes between opening this dialog and confirming it is decided THERE — and the AFTER screen
   * already follows the server (`recordedLoss` gates the notice), so the two can never contradict.
   *
   * The two clauses, read off the lobby frame we already hold:
   *   · the table did not deal a full house of humans (`fullAtDeal === false`), so nobody at it
   *     ever had a full game to walk out of — free for everyone, not just the last one out;
   *   · or every seat we are playing AGAINST is a bot. On a partnership game that is the OTHER
   *     TEAM (seat parity, the same relation the team stage arranges), so a یار still sitting
   *     beside us does not make this an abandonment; everywhere else it is every other seat.
   *
   * ⚠️ `fullAtDeal` IS OPTIONAL ON THE FRAME. A cached bundle talking to a new server always gets
   * it; a new bundle talking to an OLD server (the deploy window) does not, and `undefined` must
   * not read as «short-handed» and start promising free exits the server will still charge for.
   * Hence `=== false`, not `!fullAtDeal`.
   *
   * ⚠️ A player whose socket dropped still holds their chair for the whole reconnection grace and
   * arrives here as `isBot: false`, which is precisely right — they have not left, and everyone's
   * screen is saying so. The server asks `isBotSeat` for the same reason.
   */
  const isPartnership = (lobby?.teams ?? false) && (lobby?.seats ?? 0) === 4;
  const isOpponentSeat = (s: number): boolean =>
    seat !== null && s !== seat && (!isPartnership || s % 2 !== seat % 2);
  /**
   * ⚠️ THE SECOND CLAUSE EXISTS BECAUSE THE FIRST ONE WAS RIGHT BY ACCIDENT (2026-09-04).
   *
   * `players` lists OCCUPIED seats only, so a resumed game's empty chair appears in no roster at
   * all: the `every()` above ran over an EMPTY list and returned true. That is the right answer —
   * an empty chair holds nobody to abandon, so the exit really is free — reached by vacuous truth,
   * which means it would have gone on being "true" for any reason at all. Widen `players()` on the
   * server to include those chairs (the obvious way to make the seat chip nameable, and the first
   * thing I nearly did) and every one of them becomes a non-bot: the `every()` flips to false, the
   * dialog starts promising a recorded loss, and NOT ONE TEST GOES RED.
   *
   * So the chairs are counted instead of assumed: every opponent seat must be either a bot or one
   * the server has told us is EMPTY. Same verdict on every table that exists today — on a
   * mid-game card table nothing is ever empty, so `absent` is 0 and the two sides are equal by
   * construction — but now it is an assertion rather than a coincidence.
   *
   * ⚠️ DEPLOY GAME-SERVER BEFORE WEB. A server that predates `absentSeats` sends nothing, so a
   * stranded resume reads `0 + 0 !== 1` and the dialog warns about a loss. That is the SAFE
   * direction — it is the server that decides, `leaveIsFree` there already makes this exit free,
   * and the after-screen follows `recordedLoss` — so the worst case is a warning nobody is
   * charged for, never a promise of free that gets charged. It still lasts only as long as the
   * two services disagree, which is the ordinary deploy order for this repo.
   */
  /**
   * IS THERE A REAL OPPONENT AT THIS TABLE RIGHT NOW? — the client's copy of
   * `TableController.noRealOpponentNow`, and the ONE question that decides the exit's wording.
   *
   * A chair holds nobody real when it is a BOT's, EMPTY (a resumed game's un-returned seat — it is
   * simply absent from `players`), PAUSED for its owner, or its owner is inside their disconnect
   * GRACE. The server is the authority and re-asks at the moment of the exit; this only has to
   * agree with it, so it is written as the same shape rather than as an equivalent-looking one.
   *
   * ⚠️ FOUR WAYS, ONE `every`. It used to be «all the listed opponents are bots AND the listed +
   * absent ones account for every opponent seat», which could not see a chair whose owner was
   * merely away: a دوز table kept PLAYING through the grace, so no `paused` frame was ever sent
   * and the survivor was charged a rated loss for the whole first minute — on the one game whose
   * public door was already right. (دوز freezes at the drop since 2026-09-15 and does send one, so
   * that particular blind spot is closed at BOTH ends now; the `every` is still the rule.)
   *
   * ⚠️ AND IT IS `every`, NOT `some` — منچ is why. Four seats means three opponents, and one of
   * them dropping leaves two real people at the table. «My opponent dropped» and «leaving is free»
   * are the same sentence only at TWO seats.
   */
  /*
   * ⚠️ THE GRACE IS DELIBERATELY ABSENT FROM THIS SET (operator, 2026-09-15, reversing the call of
   * the day before). `reconnecting` — and a `paused` whose reason is a DISCONNECT — mean the
   * opponent may still be coming back, and treating that as «nobody to abandon» made the exit free
   * from the instant a socket blipped. A player in a losing position only had to wait for one.
   *
   * ⚠️ AND THE SERVER IS THE AUTHORITY, WHICH IS WHY THIS HAS TO MOVE WITH IT. `noRealOpponentNow`
   * refuses a seat in `droppedSeats` outright; if this set kept the grace, the dialog would go on
   * promising a free exit that the server then CHARGES — a live-looking free button costing a real
   * player a real rated loss, which is the one direction this can be wrong in that hurts.
   *
   * A pause whose reason is `"left"` stays: that is «خروج بدون بازگشت», a confirmed departure, and
   * there is genuinely nobody to abandon.
   */
  const awaySeats = new Set<number>([
    ...(lobby?.absentSeats ?? []).map((a) => a.seat),
    ...(paused !== null && paused.reason === "left" ? [paused.seat] : []),
  ]);
  const noRealOpponentNow =
    lobby !== null &&
    seat !== null &&
    lobby.players.length > 0 &&
    Array.from({ length: lobby.seats }, (_, s) => s)
      .filter(isOpponentSeat)
      .every((s) => {
        /*
         * ⚠️ AN UNLISTED SEAT IS NOT AN EMPTY SEAT — IT IS A SEAT WE WERE TOLD NOTHING ABOUT, and
         * the two must not collapse. The server says «empty» with `absentSeats` (folded into
         * `awaySeats` above); a roster that simply omits a chair is what an older game-server
         * sends, and reading that as «free» would promise a player something the server would then
         * charge them for. Promising nothing costs a warning nobody is charged for, which is the
         * safe direction of the same mistake. Pinned by `chess-online.test.tsx`'s roster-of-one.
         */
        const player = lobby.players.find((x) => x.seat === s);
        return (player !== undefined && player.isBot) || awaySeats.has(s);
      });
  /**
   * …AND THE THIRD WAY A CHAIR HOLDS NOBODY TO ABANDON: THE TABLE IS STOPPED FOR IT (2026-09-14).
   *
   * A شطرنج/تخته نرد seat inside its drop grace is still listed in `players` as a HUMAN — correctly,
   * the chair is still theirs — so neither `opponentsAllBots` nor `absentSeats` can see it, and the
   * dialog went on promising a recorded loss for walking away from a board nobody could move.
   * `TableController.leaveIsFree` asks the same question server-side (`isPausedFor`) and is the
   * authority; this only picks the sentence, and now it agrees.
   *
   * ⚠️ READ OFF THE `paused` FRAME THE STORE ALREADY HOLDS, deliberately — not a new lobby field.
   * `LobbyPayload` and `LobbyState` are two hand-written copies of one shape with nothing keeping
   * them in step, so a value that can be derived from a frame we already receive should be.
   * ⚠️ `paused` NAMING AN OPPONENT MEANS EXACTLY «the board is frozen for them» — `pauseSeat` is
   * entered only by the two-seat BOARD games (شطرنج/تخته نرد's «توقف», and دوز since 2026-09-15),
   * never by a card / منچ table, which keeps playing around a dropped seat.
   */
  const pausedOpponent = paused !== null && seat !== null && isOpponentSeat(paused.seat);
  /*
   * ⚠️ MID-GAME, NOT «PUBLIC AND MID-GAME» (operator, 2026-09-14, second pass). «Public only» was
   * about WHO FILLS THE CHAIR — a private host picks a `disconnectPolicy` and keeps it — and never
   * about punishing the player who stayed. Those are different questions and the first pass
   * conflated them. `publicMidGame` still gates `setPublicGameInProgress` below, which really is a
   * question about public games.
   */
  const midGame = phase === "playing";
  const lastHumanStanding =
    midGame && lobby !== null && (lobby.fullAtDeal === false || noRealOpponentNow);
  /**
   * PUBLISH IT TO THE APP ROOT — the invite prompt has to refuse while this is true.
   *
   * `InvitePrompt` is mounted above every view and cannot see a table's lobby frame, so it cannot
   * tell a public game in progress from a private table, a waiting room or the win screen. This is
   * the one derived boolean it needs, and it means exactly «leaving right now costs a recorded
   * loss». Once the game is OVER the win screen is free to leave and an invite must be acceptable
   * again — `phase` alone gives that, because `onGameOver` moves it to "gameover" and the store
   * deliberately refuses to let a late state frame flip it back (an `&& result === null` here reads
   * like belt-and-braces but no test can distinguish it, so it is left out rather than left to rot).
   *
   * ⚠️ CLEARED ON UNMOUNT, or the flag outlives the table and refuses invites in the lobby.
   */
  const setPublicGameInProgress = useSession((s) => s.setPublicGameInProgress);
  useEffect(() => {
    setPublicGameInProgress(publicMidGame);
  }, [publicMidGame, setPublicGameInProgress]);
  useEffect(() => () => setPublicGameInProgress(false), [setPublicGameInProgress]);
  /*
   * «انصراف» — A PRIVATE TABLE FOR TWO (operator, session B item 4, 2026-10-08).
   *
   * There «خروج بدون بازگشت» was never «a bot takes my seat and the others play on» — there are no
   * others, and the server FORFEITS (`forfeitTwoSeatTable`, reason «resign»): a rated loss the
   * private leaver was never told about. So the button is «انصراف», and because «انصراف» is this
   * app's word for CANCEL everywhere else, it opens a SECOND confirmation that says what will
   * happen. The outcome is byte-for-byte today's `leave-forever` door (`doLeaveForever`).
   *
   * ⚠️ THE BODY MIRRORS THE SERVER'S `noRealOpponentNow`, never `lastHumanStanding`: that one's
   * `fullAtDeal === false` is a PUBLIC courtesy, and a private table that dealt short-handed still
   * forfeits against a human. The client's copy (above) already agrees with the server where it can
   * see: a bot's chair or an EMPTY one holds nobody → nothing recorded; a human's — there, inside
   * their drop grace, or gone but claimable (the chair is still theirs) → a LOSS. Where it cannot
   * see (a frozen board after the grace) it says «loss»: a warning nobody is charged for is the
   * safe way to be wrong. Four-seat private tables and every PUBLIC dialog are untouched.
   */
  const twoSeatPrivate = lobby?.isPrivate === true && lobby.seats === 2;
  const [confirmingResign, setConfirmingResign] = useState(false);
  // It belongs to THIS match: the result (or a new store under the same container — App keys
  // nothing on the join) takes it away for good, so it can never pop up by itself in the next one.
  useEffect(() => {
    if (!midGame) setConfirmingResign(false);
  }, [midGame]);
  /*
   * AN IN-APP WAY OFF THIS TABLE, PARKED BY `setView` (card-a5 item 1) — a toast's tap, the reconnect
   * overlay's «خروج». At a public table in progress it gets «خروج»'s own confirmation (the dialog below
   * reads `pendingExit` for where to go once left); anywhere else nothing is at stake and it just goes.
   */
  useEffect(() => {
    if (pendingExit === null) return;
    if (publicMidGame) setConfirmingLeave(true);
    else leaveTable(pendingExit.to);
  }, [pendingExit, publicMidGame, leaveTable]);
  // …and it belongs to THIS table: it dies with it (review fix). The session store outlives a crash,
  // so a destination parked while the question was up would otherwise greet the table the crash
  // door brings back — still «connecting», so not «in progress» — and walk the player straight off
  // it (and keep the reconnect overlay hidden until then).
  useEffect(() => () => cancelTableExit(), [cancelTableExit]);
  /*
   * AN ACT ELSEWHERE, ROUTED THROUGH THIS TABLE (card-a5 item 1): the player started or joined another
   * game from the lobby while this device held a seat here, so the app came back first. Once the table
   * has shown itself: a public game in progress → «خروج»'s confirmation (confirm leaves exactly as
   * «خروج» leaves, then does the act; cancel stays); anything else (the match over, the seat gone, a
   * table that is not public after all) → nothing to leave, the act just happens. Asked ONCE per table.
   */
  const exitThen = onlineJoin.kind === "rejoin" ? onlineJoin.then : undefined;
  const [thenAsking, setThenAsking] = useState(false);
  const thenSettled = useRef(false);
  useEffect(() => {
    thenSettled.current = false;
    setThenAsking(false);
  }, [store]);
  useEffect(() => {
    if (exitThen === undefined || thenSettled.current) return;
    const nothingToLeave = (): void => {
      thenSettled.current = true;
      runExitIntent(exitThen);
    };
    if (phase === "error" || phase === "gameover") return nothingToLeave();
    if (phase !== "playing" || lobby === null) return; // not shown itself yet
    if (!publicMidGame) return nothingToLeave();
    thenSettled.current = true;
    setThenAsking(true);
    setConfirmingLeave(true);
  }, [exitThen, phase, lobby, publicMidGame, runExitIntent]);
  // …and when the table stops being a public game in progress WHILE that question is up (the match
  // ended under it — review fix): nothing is at stake any more, so the act just happens, as a parked
  // exit just goes (above). Never after a confirm or a cancel: both clear `thenAsking` first.
  useEffect(() => {
    if (!thenAsking || publicMidGame || exitThen === undefined) return;
    setThenAsking(false);
    setConfirmingLeave(false);
    runExitIntent(exitThen);
  }, [thenAsking, publicMidGame, exitThen, runExitIntent]);
  const exitDialog = confirmingLeave ? (
    publicMidGame ? (
      <ConfirmDialog
        title={fa.confirm.exitPublicTitle}
        body={lastHumanStanding ? fa.confirm.exitPublicLastBody : fa.confirm.exitPublicBody}
        confirmLabel={fa.confirm.leavePublic}
        cancelLabel={fa.confirm.stayPublic}
        danger
        onConfirm={() => {
          setConfirmingLeave(false);
          // WHERE TO, once left (card-a5 item 1): an act elsewhere routed through this table → that
          // act; an in-app way off the table (a toast, the overlay's exit) → its screen; the table's own
          // «خروج» → the lobby. One exit, one record, one cost — only the destination differs.
          const intent = thenAsking ? exitThen : undefined;
          const to = pendingExit?.to;
          setThenAsking(false);
          if (to !== undefined) cancelTableExit(); // the destination rides the closure below
          doLeaveForever(
            intent !== undefined
              ? () => runExitIntent(intent)
              : to !== undefined
                ? () => leaveTable(to)
                : undefined,
          );
        }}
        onCancel={() => {
          setConfirmingLeave(false);
          // «برمی‌گردم به بازی» — they stay; whatever was waiting on the exit is dropped.
          if (pendingExit !== null) cancelTableExit();
          if (thenAsking) {
            setThenAsking(false);
            clearPendingInvite(); // a friend invite riding a «create» that will not happen now
          }
        }}
      />
    ) : phase === "playing" ? (
      <ConfirmDialog
        body={twoSeatPrivate ? fa.confirm.exitOnlineTwoSeatBody : fa.confirm.exitOnlineBody}
        confirmLabel={fa.confirm.leaveOnlineResumable}
        danger={false}
        onConfirm={() => {
          setConfirmingLeave(false);
          doLeave();
        }}
        secondaryLabel={twoSeatPrivate ? fa.confirm.resign : fa.confirm.leaveOnlineForever}
        onSecondary={() => {
          setConfirmingLeave(false);
          // «انصراف» never resigns on this tap — it asks again (see `confirmingResign`).
          if (twoSeatPrivate) setConfirmingResign(true);
          else doLeaveForever();
        }}
        onCancel={() => setConfirmingLeave(false)}
      />
    ) : (
      <ConfirmDialog
        onConfirm={() => {
          setConfirmingLeave(false);
          doLeave();
        }}
        onCancel={() => setConfirmingLeave(false)}
      />
    )
  ) : null;
  // The SECOND confirmation, while the match is still live — a result arriving under it takes it
  // away (its «باخت ثبت می‌شه» would no longer be true).
  const confirmDialog =
    confirmingResign && midGame ? (
      <ConfirmDialog
        title={fa.confirm.resignTitle}
        body={noRealOpponentNow ? fa.confirm.resignFreeBody : fa.confirm.resignLossBody}
        confirmLabel={fa.confirm.resignConfirm}
        cancelLabel={fa.confirm.resignCancel}
        danger
        onConfirm={() => {
          setConfirmingResign(false);
          doLeaveForever();
        }}
        onCancel={() => setConfirmingResign(false)}
      />
    ) : (
      exitDialog
    );
  /*
   * «بازی دوباره» — FOUR DIFFERENT THINGS, AND ONLY ONE OF THEM IS THE OLD ONE.
   *
   * On a PUBLIC table it is unchanged: tear this connection down and quick-match again. On a
   * PRIVATE table it is the host's call (operator, 2026-09-09) — the host asks the server to open
   * the successor, and everyone (the host included) then FOLLOWS the one `rematch` frame into it,
   * keeping their seat. Following, rather than the host jumping straight in, is what puts them all
   * at the same table: it used to leave the host alone in a new empty room while the guests were
   * told «این بازی تمام شده است».
   *
   * `rematchAction` is the single decision, shared with the overlays so the button they render and
   * the behaviour they trigger cannot disagree.
   *
   * SESSION B (operator, 2026-10-08): «a rematch button shows only when it can work». A private
   * one is HIDDEN, with one line saying why, when the owner has left, when everybody else has, or
   * when our own socket to the finished room is gone — read off the server's verdict
   * (`rematch-state`) when it has spoken, and off what this client can see for itself when it has
   * not (an OLD game-server never speaks): the roster's bot flips and the `left` frames the store
   * recorded after the end. The owner presses ONCE (offer + follow), and a PUBLIC table's button is
   * a fresh quick-match whatever door the player came in by.
   */
  // Who sat where when the match ENDED (frozen) — against who is still there now (live).
  const endRoster = (endLobby ?? lobby)?.players ?? [];
  const liveRoster = lobby?.players ?? [];
  const goneSinceEnd = (s: number): boolean =>
    liveRoster.some((p) => p.seat === s && p.isBot) || endDepartures.some((d) => d.seat === s);
  /*
   * THE OWNER IS WHOEVER HOLDS THE CROWN NOW (card-a5 item 8, operator 2026-10-11: «the crown
   * passes to a guest after the owner's reconnect grace, as before»). After the end the game-server
   * hands it on once the owner has gone — when their grace runs out, or at once when they closed
   * the table — and the guest it lands on (the HEIR) owns the rematch from then on. Session B had
   * frozen it at the first `gameover`, which left every guest a dead button for good.
   */
  const isOwner = isHost;
  const ownerSeatAtEnd = endRoster.find((p) => p.isHost === true)?.seat;
  // UNKNOWN fallback — the table HAD an owner at the end, and now no human chair wears the crown.
  // A game-server that hands it on puts it on the heir's chair in the roster; one that cannot leaves
  // no chair wearing it. A roster that never showed a crown says nothing, and hides nothing.
  const ownerGone =
    ownerSeatAtEnd !== undefined && !liveRoster.some((p) => p.isHost === true && !p.isBot);
  // «…or the match had no other human» — an owner who played bots alone keeps the offer, so an
  // empty list must never read as «everybody left». An HEIR is asked the same question as any owner
  // (the game-server's `rematchState`): the owner who left is one of «the others».
  const endOthers = endRoster.filter((p) => !p.isBot && p.seat !== seat);
  const othersGone = endOthers.length > 0 && endOthers.every((p) => goneSinceEnd(p.seat));
  /** The offered successor turned out not to be joinable (asked on the press) — and why. */
  const [successorRefusal, setSuccessorRefusal] = useState<{
    roomId: string;
    line: string;
  } | null>(null);
  const rematch = rematchAction({
    isPrivate: lobby?.isPrivate === true,
    isOwner,
    offerRoomId: rematchRoomId,
    rematchState: rematchState ?? "unknown",
    roomAlive: !roomGone,
    ownerSeatBotted: ownerGone,
    everyOtherHumanBotted: othersGone,
    successorJoinable:
      successorRefusal !== null && successorRefusal.roomId === rematchRoomId ? false : "unknown",
  });
  /*
   * FOLLOW THE OFFER — after asking whether the successor still takes players (`02-rematch.md`
   * R8: expired ten minutes after the offer, dealt without us, or closed by its host — each used to
   * end on an error screen). Not joinable → the button goes and the refusal line stays ON the end
   * card. A lookup that fails never stands between a player and the table: that is today's
   * behaviour, and the join itself still says what is wrong if anything is.
   *
   * ⚠️ ONE AT A TIME. The press now waits on a round trip, and a second tap inside it must not
   * join twice.
   */
  const following = useRef(false);
  const follow = (roomId: string): void => {
    if (following.current) return;
    following.current = true;
    // Our OLD seat is the preference — the server honours it while it is free, so a حکم
    // partnership survives the rematch. Changing `onlineJoin` recreates the store, whose
    // unmount effect disconnects this one.
    const go = (): void => joinPrivate(gameId as GameId, roomId, seat ?? undefined);
    void api.getTableStatus(roomId).then(({ state }) => {
      if (state === "waiting" || state === "unknown") {
        go();
        return;
      }
      following.current = false;
      setSuccessorRefusal({ roomId, line: successorRefusalLine(state) });
    }, go);
  };
  /*
   * THE OWNER PRESSES ONCE (`02-rematch.md` R9; what the 2026-09-10 design already said). Their
   * press asks for the successor, and when the offer lands they follow it like everybody else — it
   * used to take a second press, and guests who followed first sat in a room whose host never came.
   * Only an offer THIS screen asked for is followed on its own; one nobody here pressed for stays
   * the owner's to take.
   */
  const followOwnOffer = useRef(false);
  const followRef = useRef(follow);
  followRef.current = follow;
  useEffect(() => {
    if (!followOwnOffer.current || rematchRoomId === null) return;
    followOwnOffer.current = false;
    followRef.current(rematchRoomId);
  }, [rematchRoomId]);
  /*
   * ⚠️ THE CONTAINER OUTLIVES ITS STORE. Following changes `onlineJoin`, which builds a NEW store
   * for the successor under this same mounted component (App renders `<XOnlineGame />` with no key
   * on the join) — so everything above that belongs to ONE match starts over with the new store, or
   * the second rematch of a sitting is dead: the first follow's one-at-a-time guard would still be
   * holding the door.
   */
  useEffect(() => {
    following.current = false;
    followOwnOffer.current = false;
    setSuccessorRefusal(null);
  }, [store]);
  const again = (): void => {
    if (rematch.kind === "join") {
      follow(rematch.roomId);
      return;
    }
    if (rematch.kind === "offer") {
      followOwnOffer.current = true;
      requestRematch();
      return;
    }
    // inert — the button is disabled or not there at all; this is belt-and-braces
    if (rematch.kind === "wait" || rematch.kind === "hidden") return;
    /*
     * PUBLIC — A FRESH QUICK-MATCH OF THIS GAME, WHATEVER DOOR WE CAME IN BY (session B). Rebuilding
     * the store from the same `onlineJoin` replayed a dead door: a friend invite or a table link
     * (`join`) re-ran `joinById(<the finished room>)` → «این بازی تمام شده است»; a «ادامه بازی»
     * return (`rejoin`) reconnected with a spent token for twelve seconds; a chess DB resume
     * (`resume`) asked for a row the terminal had deleted (R17–R19). Only a quick-match arrival
     * was ever re-queued.
     */
    store.getState().disconnect();
    if (onlineJoin.kind === "quick") setRound((r) => r + 1);
    else playOnline(gameId as GameId);
  };

  /*
   * card-9f — THE «بازی عمومی» REFUSAL ASKS THE GAME-SERVER WHERE THE HELD SEAT IS (the operator,
   * 2026-10-28: «the refusal's two options really work»). This device's own way back (`refusalWayBack`)
   * covers only a phone that kept the seat's ticket; the game-server knows the table for any device, by
   * this launch's initData (`askHeldSeat`). Asked ONCE per refusal of THIS store; the answer is kept
   * WITH the store it answered for, so a later store (a retry, «بازی دوباره») never reads it; an answer
   * landing after the screen went away is dropped (the abort, and `current`). Keyed on a boolean, a
   * string and the store — never on a frame object (CLAUDE.md, the effect gotcha).
   */
  const refusedForHeldSeat = phase === "error" && isPublicGameInProgressMessage(error);
  const [heldSeat, setHeldSeat] = useState<{ store: object; lookup: HeldSeatLookup } | null>(null);
  useEffect(() => {
    if (!refusedForHeldSeat) return;
    const asking = new AbortController();
    let current = true;
    void askHeldSeat(GAME_SERVER_URL, initData ?? "", { signal: asking.signal }).then((lookup) => {
      if (current) setHeldSeat({ store, lookup });
    });
    return () => {
      current = false;
      asking.abort();
    };
  }, [refusedForHeldSeat, initData, store]);
  /*
   * …AND ONE TAP TAKES IT (card-9f): a second door join from this page while the first one's socket is
   * live would reach the game-server's same-launch door, which lets every OTHER socket of the player go —
   * this page's first one included. So the first tap of «بازگشت…» or «خروج» makes both inert, at once
   * (the ref) and on screen (the state); a new store starts over.
   */
  const heldTapped = useRef(false);
  const [heldTappedOnce, setHeldTappedOnce] = useState(false);
  useEffect(() => {
    heldTapped.current = false;
    setHeldTappedOnce(false);
  }, [store]);

  // The verdict on a public-table quit — the connection is already gone, so this owns the screen
  // until they acknowledge it (the `IdleRemovedNotice` pattern).
  if (autoReturnFailed || routedReturnFailed) return null; // on its way elsewhere (the effects above)
  if (leavePenalty) {
    return (
      <LeavePenaltyNotice
        info={leavePenalty.info}
        onAcknowledge={() => {
          const then = leavePenalty.then;
          setLeavePenalty(null);
          then();
        }}
      />
    );
  }
  if (channelGate && !overlayActive) return channelGate;
  // LOCKED OUT of public games — the gate refused the join with its own Persian sentence (which
  // carries the remaining time). Recognised by the SHARED detector, so the two sides cannot drift.
  if (phase === "error" && !overlayActive && isLeaveLockoutMessage(error)) {
    return <LockoutNotice message={error ?? ""} onAcknowledge={() => setView("lobby")} />;
  }

  /*
   * «تو همین حالا در یک بازی عمومی هستی…» — THE JOIN WAS REFUSED BECAUSE A PUBLIC GAME OF OURS IS STILL
   * GOING (card-a5 item 1: the server refuses another public game while a seat is held; live-tables
   * refuses the same game while seated). The sentence says why; this offers the way back to that table:
   * the one the GAME-SERVER names (card-9f, any device — `named` below, with «خروج»), else, while it has
   * not answered or cannot tell (an older game-server), the one THIS device kept a ticket for (today's
   * door). The same words as the maintenance and crash doors.
   */
  const wayBack = refusedForHeldSeat ? refusalWayBack() : null;
  /** The held seat the GAME-SERVER named for this refusal (card-9f) — null until it answers, and for
   *  «nothing held» / «cannot tell» (an older game-server): today's screen then, exactly. */
  const named =
    refusedForHeldSeat && heldSeat !== null && heldSeat.store === store
      ? heldSeat.lookup.kind === "held"
        ? heldSeat.lookup
        : null
      : null;
  /** Back to the named seat — with `then`, the refusal's «خروج» (card-a5's return-then-act). Once. */
  const goToNamedSeat = (then?: ExitIntent): void => {
    if (named === null || heldTapped.current) return;
    heldTapped.current = true;
    setHeldTappedOnce(true);
    returnToHeldSeat(named.game, named.roomId, then);
  };
  // …AND A NEWER COPY OF THE APP TOOK THIS SEAT BACK (card-74): the same screen, its own sentence —
  // the table underneath is the other copy's now, so it covers everything (its «باشه» → the lobby).
  if ((phase === "error" || takenOver) && !overlayActive) {
    return (
      <div
        style={{
          height: "var(--app-h)",
          display: "grid",
          placeItems: "center",
          background: "var(--room-bg)",
          padding: 24,
          textAlign: "center",
        }}
      >
        <div style={{ display: "flex", flexDirection: "column", gap: 16, alignItems: "center" }}>
          <p style={{ color: "var(--danger)", margin: 0, fontWeight: 700 }}>
            {takenOver ? fa.online.takenOver : (error ?? fa.online.connectionError)}
          </p>
          {/* …AND HOW IT ENDED (session B, item 3): a player coming back to a table that is over
              reads «این بازی تمام شده است» and then, when the server recorded it, one line of how
              — their own result and facts about the table. No `end` (an old api) → the first
              line alone. */}
          {tableEnd && tableEndLine(tableEnd) ? (
            <p style={{ color: "var(--hint)", margin: 0, lineHeight: 1.6 }}>
              {tableEndLine(tableEnd)}
            </p>
          ) : null}
          {/* An ACKNOWLEDGEMENT, not an exit. This screen is most often reached by an invite
              that could not be taken (the table filled, dealt, or ended), where there is no game
              to «خروج» from — and a notice with no clear way to dismiss it just lingers. (The
              game-server's channel refusal no longer lands here: `useTableChannelGate`, above.) */}
          {/* card-9f — the table the GAME-SERVER named: back to it from any device, and «خروج» (back,
              then the table's own public «خروج» confirmation, then the lobby). A public waiting room
              gets no «خروج»: nothing is at stake before the deal, and the room has its own exit. */}
          {named ? (
            <>
              <Button variant="primary" disabled={heldTappedOnce} onClick={() => goToNamedSeat()}>
                {named.waiting ? fa.maintenance.returnToTable : fa.maintenance.returnToGame}
              </Button>
              {named.waiting ? null : (
                <Button
                  variant="ghost"
                  disabled={heldTappedOnce}
                  onClick={() => goToNamedSeat({ kind: "lobby" })}
                >
                  {fa.online.leave}
                </Button>
              )}
            </>
          ) : wayBack ? (
            <Button
              variant="primary"
              onClick={() => rejoinOnline(wayBack.game, wayBack.roomId, wayBack.token)}
            >
              {wayBack.waiting ? fa.maintenance.returnToTable : fa.maintenance.returnToGame}
            </Button>
          ) : null}
          <Button variant={named || wayBack ? "ghost" : "primary"} onClick={() => setView("lobby")}>
            {fa.online.acknowledge}
          </Button>
        </div>
      </div>
    );
  }

  // WE WERE REMOVED FOR INACTIVITY. This outranks EVERYTHING, including a host-close: the table
  // underneath is no longer ours — the server has given the seat to a bot and will REFUSE us if we
  // try to come back — so every other screen it could render describes a game we are not in.
  // Acknowledge → straight to the lobby.
  // THE HOST REMOVED US («اخراج»). Same standing as an idle removal — the table underneath is no
  // longer ours and the server will refuse every way back — so it outranks every other screen, and
  // the saved re-join ticket goes with it (see the idle note below for the same argument; here it
  // matters more, because a stashed ticket is now also what the invite-link fallback would try).
  if (kickedOut) {
    clearOnlineGame();
    return (
      <KickedNotice
        onAcknowledge={() => {
          acknowledgeKick();
          doLeave();
        }}
      />
    );
  }

  if (removedForIdle) {
    /*
     * AN IDLE REMOVAL IS FINAL — ON EVERY GAME, تخته نرد INCLUDED (operator's call, 2026-08-15).
     *
     * The room drops our reservation, `onAuth` refuses a fresh join and `onLeave` refuses to mint a
     * new one, so keeping the saved seat would advertise a door that is locked. تخته نرد used to be
     * an exception here (the seat stayed reclaimable); it now follows منچ and the card tables — see
     * `abandonToBot` for why the exception was withdrawn.
     */
    clearOnlineGame();
    return (
      <IdleRemovedNotice
        onAcknowledge={() => {
          acknowledgeRemoval();
          doLeave();
        }}
      />
    );
  }

  // THE HOST CLOSED THE TABLE. It no longer exists (the server has already emptied the room), so
  // this outranks every other screen: say who closed it AND why, and wait for them to acknowledge
  // it. It fires in the waiting room AND mid-game — the host's permanent «خروج بدون بازگشت»
  // cancels the whole table. (An older comment here said the mid-game case only bots their seat;
  // that stopped being true when host-cancel shipped, and a player whose live game disappeared is
  // exactly who most needs to be told what happened.)
  if (closedBy !== null) {
    return <TableClosedNotice by={closedBy} midGame={closedMidGame} onLeave={doLeave} />;
  }
  // The table auto-expired (host never started it within the idle window). Same precedence as a
  // host-close: outrank every other screen, show «میز منقضی شد», then return to the lobby.
  if (expiredByIdle) {
    return <TableExpiredNotice onLeave={doLeave} />;
  }

  if (phase === "connecting") {
    /*
     * THE WAY OUT OF «در حال اتصال…» (session B, item 8). There was none on any online game, so a
     * hung join — or the whole twelve-second «ادامه بازی» patience — trapped the player. It is a
     * DROP (`store.drop`), never `doLeave`: the join is called off, whatever landed closes the way a
     * dropped socket closes (the seat's grace, nothing charged), and the saved ticket stays. A
     * consented close here would be «خروج» — at a private شطرنج/تخته نرد, the claimable-at-once door.
     */
    return (
      <Boot
        caption={fa.online.connecting}
        onLeave={() => {
          store.getState().drop();
          setView("lobby");
        }}
      />
    );
  }

  // Artwork gate for the FELT only — the waiting room needs no card faces, so it is never held.
  // Reaching here with the chunk still outstanding means the after-boot prefetch has not finished
  // (a table deep-link opened straight into a game, or the prefetch failed and is retrying).
  if (needsFaces && !faces.ready && phase !== "waiting" && !feltShown.current) {
    return faces.failed ? (
      <Boot error={fa.status.offline} onRetry={faces.retry} onLeave={doLeave} />
    ) : (
      <Boot
        caption={fa.status.preparingCards}
        onLeave={() => {
          store.getState().leaveWaiting(); // card-98 3.3 — no cards on this phone yet
          doLeave();
        }}
      />
    );
  }

  if (phase === "waiting" || !view) {
    return (
      <>
        <WaitingRoom
          lobby={lobby}
          seat={seat}
          isHost={isHost}
          gameId={gameId}
          onStart={(intent) => store.getState().start(intent)}
          onSetTeams={(order) => store.getState().setTeams(order)}
          onLeave={leave}
          onPlaySolo={playSolo}
          title={`${fa.online.play} ${title}`}
          // PUBLIC tables only — the waiting room decides which seats may show a «+»; the
          // container just opens the picker and reports my one live invite back to it.
          {...(lobby?.isPrivate === false
            ? { onInviteSeat: (s: number) => setSeatInvite({ seat: s }) }
            : {})}
          pendingInvite={myTableInvite}
          onCancelInvite={(id) =>
            void sessionStore
              .getState()
              .cancelGameInvite(id)
              .catch(() => {})
          }
        />
        {/* The HOST leaving the waiting room is announced HERE too — the crown has just
            moved, and whoever inherited it needs to know the table is theirs to start.
            (The notice used to render only on the playing screen, so a pre-start host
            departure was completely silent.) */}
        {/* The friend picker — the SAME sheet the mid-game bot-seat invite uses. Here it sends
            an ORDINARY invite: no seat reservation, so the friend takes whatever chair is free
            when they arrive and quick-match fill is completely unaffected. Nothing here says when
            the invite dies: the API derives that from this table's own published countdown, so it
            closes one second before the deal and follows the countdown pausing and resuming
            without this client sending anything. */}
        {seatInvite && lobby?.roomId ? (
          <SeatInviteSheet
            players={lobby.players}
            onPick={(userId) => {
              void sessionStore
                .getState()
                .sendGameInvite(userId, gameId as GameId, lobby.roomId!, {
                  // The chair the «+» was tapped on, as a PREFERENCE — no reconnect token, so
                  // nothing is held: the server seats them here only if it is still free when
                  // they arrive, else in the lowest free one. On حکم/شلم this is what makes
                  // «sit opposite me» mean «be my partner» (seat parity IS the team).
                  seat: seatInvite.seat,
                })
                .catch(() => {});
            }}
            onClose={() => setSeatInvite(null)}
          />
        ) : null}
        {leftNotice ? <LeftToast notice={leftNotice} isHost={isHost} /> : null}
        {/* The host pressed «شروع» and the server said no (maintenance stops new games): say why. */}
        <TopNoticeStack>
          {startRefused ? <StartRefusedNotice message={startRefused} /> : null}
        </TopNoticeStack>
        {confirmDialog}
      </>
    );
  }

  // playing | gameover — both render the live table; gameover adds the overlay,
  // and the turn timer / paused overlay layer on top during a live match.
  const timerSec = settings?.turnTimerSec ?? 0;
  // The turn timer only makes sense with at least one OTHER real player at the table.
  // If the user is effectively alone with bots (everyone else left a public table),
  // treat it like a solo/private game — no timer (which also prevents the reported
  // countdown frozen at 0 once all remaining turns are bots').
  const otherHumans = (lobby?.players ?? []).filter((p) => !p.isBot && p.seat !== seat).length;
  // The live timer is threaded INTO the table (rendered on the felt) so it anchors to the
  // play area, not the shell. Null unless there's a timed turn, not paused/over. (A shared
  // passing window comes through as turn.seat < 0.)
  //
  // ITEM 5 — YOUR OWN turn always shows its countdown, even where every other seat is a bot.
  // The old rule demanded another human, which is why online منچ looked like it had no timer:
  // a quick-match fills with bots, so the ring never rendered — while the SERVER went on
  // arming the clock and charging misses. Hiding a deadline that is still enforced is the
  // worst of both. Someone ELSE's turn still needs a real opponent, which is what keeps the
  // ring from sitting frozen through an endless run of bot turns.
  const timed = otherHumans > 0 || (seat !== null && turn?.seat === seat);
  const timer: TableTurnTimer | null =
    turn && timerSec > 0 && timed && !paused && !result
      ? { turn, you: seat, totalSec: timerSec }
      : null;
  // FEATURE 3 — the host may invite a friend into a bot chair only while the match is live.
  const onInviteSeat =
    isHost && phase === "playing" && !result
      ? (targetSeat: number) => setSeatInvite({ seat: targetSeat })
      : undefined;
  // The OpponentAwayOverlay (reconnecting grace → pause → claim) is used by BOTH 2-player
  // perfect-info games — chess AND backgammon — for a mid-game disconnect, INSTEAD of the card
  // PausedOverlay (whose resume-with-bot button never applies — a bot never plays a 2-player human
  // side). `awayFa` selects the matching overlay strings (identical wording today).
  //
  // ⚠️ `paused` and `claimable` are still chess/تخته نرد-only, but `reconnecting` is NOT any more —
  // the server announces a drop for EVERY game now, so a card table gets the frame too and renders
  // the non-blocking `ReconnectingToast` below instead of this scrim. An earlier comment here said
  // the card path was "completely inert"; that stopped being true when the announce was un-gated.
  /*
   * ⚠️ دوز JOINED THIS FAMILY ON 2026-09-15, AND THE SERVER CHANGE IS WHY IT HAD TO. A دوز table
   * now FREEZES at the drop like the other two two-seat board games, so it sends a `paused` frame
   * where it never used to — and the branch below this one hands an unrecognised `paused` to
   * `PausedOverlay`, a full-screen scrim carrying a host «ادامه با ربات» button that means nothing
   * at a table for two. The pill is what a frozen two-seat board shows.
   * ⚠️ DEPLOY GAME-SERVER AFTER WEB, or the other way round a cached old bundle gets the new
   * `paused` frame and paints that scrim.
   */
  const usesAwayOverlay = gameId === "chess" || gameId === "backgammon" || gameId === "dooz";
  const awayFa = gameId === "backgammon" ? fa.backgammon : fa.chess;
  /*
   * ⚠️ ON A CARD TABLE THE RECONNECT NOTICE IS ON THE SEAT, NOT IN THE STACK (operator, 2026-09-15).
   * `TopNoticeStack` sits at `safeTop + 58px`, a height chosen for the two-seat board games' 40 px
   * header band; on every card table that is the felt. Measured on real staging tables
   * (`probe-card-pill.cjs`) the pill covered حکم's trick counter, trump pill and partner for a whole
   * 60 s grace, بی‌دل's round counter, and on شلم and بلوف the «خروج» button. No spot off the felt
   * covers nothing at 320/360, so the table says it on the absent seat's own label instead — the
   * card tables' equivalent of the شطرنج pill sitting on the absent opponent's strip.
   * Derived from `GAME_CATEGORY`, so a card game added later takes the same path — and must render
   * `reconnectingSeat`, or it has no reconnect notice at all.
   */
  const seatCarriesReconnect = GAME_CATEGORY[gameId as GameId] === "card";
  /*
   * ⚠️ THE CROWN IS THE ONE THING A SEAT CANNOT SAY (2026-09-15). `LeftToast` carries three facts:
   * who lost the chair and how, that a bot has it and play goes on, and — only for whoever
   * inherited it — that the TABLE is theirs now. The first two are about a chair and move onto it;
   * the last is about the READER, so that one case keeps the toast. It is a private table's host
   * leaving mid-game, and nothing else on the screen says the crown moved.
   */
  const crownMoved = leftNotice !== null && leftNotice.wasHost === true && isHost;
  /*
   * ⚠️ ONE PREDICATE FOR BOTH HALVES, so a departure can never fall through both doors or neither.
   * `botTook === false` is the pre-start shape — the chair is simply free, there is nobody sitting
   * there to label — and it keeps the toast, as does the crown and every non-card table.
   */
  const leftGoesToSeat =
    seatCarriesReconnect &&
    leftNotice !== null &&
    leftNotice.botTook !== false &&
    !crownMoved &&
    !result;
  /*
   * ⚠️ AT MOST TWO, AND NEVER BOTH FOR ONE SEAT. The store clears `reconnecting` in the same update
   * that sets `leftNotice`, so a seat's own grace and its takeover cannot overlap — but ANOTHER
   * seat's departure can still be on screen while this one is in its grace, which is why this is a
   * list. `botTook === false` is pre-start (no bot, the chair is simply free) and is left to the
   * toast; `dim` marks the chair as waiting, never one a bot is already playing.
   */
  const seatNotices: readonly SeatNotice[] =
    !seatCarriesReconnect || result
      ? []
      : [
          ...(reconnecting
            ? [
                {
                  seat: reconnecting.seat,
                  text: fa.online.seatReconnecting,
                  label: fa.online.reconnectingSeat(seatName(lobby?.players, reconnecting.seat)),
                  dim: true,
                },
              ]
            : []),
          ...(leftGoesToSeat && leftNotice
            ? [
                {
                  seat: leftNotice.seat,
                  text: seatLeftText(leftNotice.reason),
                  label: leftSentence(leftNotice, leftNotice.name),
                  dim: false,
                },
              ]
            : []),
        ];
  /**
   * THE SCRIM'S OWN WAY OUT — offered only while leaving really is free.
   *
   * The overlay covers the table's exit button, so without this the player has no door at all for
   * the length of the grace. It opens the SAME confirm dialog the table's exit does (`leave`), so
   * there is one exit flow and one place that decides the wording; `lastHumanStanding` is what makes
   * that wording the free one, and the server re-asks the same question at the moment of the exit.
   *
   * ⚠️ GATED ON `lastHumanStanding`, NOT SHOWN UNCONDITIONALLY. A private table's «توقف»/«ادعا»
   * still freezes for an opponent who is genuinely coming back and whose seat is still theirs —
   * nothing there is free, and a button captioned «باخت ثبت نمی‌کند» would be a lie. Those tables
   * keep the overlay exactly as it was.
   */
  /*
   * ⚠️ THE SCRIM'S OWN EXIT IS GONE BECAUSE THE SCRIM IS (operator, 2026-09-15). It existed only
   * because a full-screen overlay covered the table's real exit at `z-10`; with the overlay
   * replaced by a `pointer-events:none` pill, that exit is reachable again and a second one beside
   * it would be a second way to do the same thing. The free exit is ANNOUNCED by `FreeExitNotice`
   * and TAKEN through the table's own button, whose dialog follows `leaveIsFree`.
   */
  feltShown.current = true;
  return (
    // The whole live table + win overlay renders with the OWNER's chosen deck (the
    // shared Card reads it via context — no per-card threading).
    <CardDeckContext.Provider value={drawDeck}>
      {renderTable({
        view,
        legal,
        send,
        leave,
        players: namedPlayers,
        timer,
        clock,
        isHost,
        onInviteSeat,
        settings: settings ?? undefined,
        absentSeats: lobby?.absentSeats,
        opponentAway: pausedOpponent,
        leaveIsFree: lastHumanStanding,
        seatNotices,
      })}
      {seatInvite ? (
        <SeatInviteSheet
          players={lobby?.players}
          onPick={(userId) => store.getState().mintSeatToken(seatInvite.seat, userId)}
          onClose={() => setSeatInvite(null)}
        />
      ) : null}
      {/* Table chat + players sheet: a header-band toggle (above every table
          layer) opening the shared bottom Sheet — chat (text + voice, correlated
          to table-mates by this table's Colyseus roomId) and the live roster
          with per-player friend actions. */}
      {lobby?.roomId ? (
        <TableChat
          wsUrl={GAME_SERVER_URL}
          tableRoomId={lobby.roomId}
          initData={initData}
          selfId={selfId}
          title={title}
          players={namedPlayers ?? lobby.players}
          selfSeat={seat}
          chatAllowed={chatAllowed}
          // «اخراج» — the roster rows carry it for the private HOST only. `isHost` comes from the
          // server's own `seat` frame, and the server re-checks it on the message, so this is the
          // affordance rather than the authorization.
          isHost={isHost}
          onKick={kick}
          // A report sheet up on OUR turn says so: a timed turn plays itself, and on a public
          // table five in a row remove the seat — a report must never cost the table.
          myTurn={seat !== null && turn?.seat === seat && !paused && !result}
        />
      ) : null}
      {showEnd && result
        ? renderWin({
            result,
            view,
            again,
            leave: doLeave,
            players: namedPlayers,
            absentSeats: (endLobby ?? lobby)?.absentSeats,
            againDisabled: rematch.disabled,
            againHidden: rematch.kind === "hidden",
            /*
             * THE HINT IS SWAPPED, NOT DROPPED. «wait» explains why the button is inert; «join»
             * says the offer has LANDED — because nothing else announces it, and a line that
             * simply disappeared would leave the arrival invisible to anyone not watching the
             * button. Guest only: `rematchOffered` speaks about the host in the third person,
             * and the host never had the waiting line to begin with. The HOST's own line is the
             * server's reason when it refused to open the successor (maintenance) — the result
             * card sits above the notice stack, so this is the one place the host would see it.
             * A HIDDEN button leaves its line behind: the reason there is no rematch (session B).
             */
            ...(rematch.kind === "hidden"
              ? {
                  againHint:
                    rematch.why === "owner-left"
                      ? fa.online.rematchOwnerLeft
                      : rematch.why === "others-left"
                        ? fa.online.rematchOthersLeft
                        : rematch.why === "connection"
                          ? fa.online.rematchConnectionLost
                          : (successorRefusal?.line ?? fa.invite.roomGone),
                }
              : rematch.kind === "wait"
                ? { againHint: fa.online.rematchWaiting }
                : rematch.kind === "join" && !isOwner
                  ? { againHint: fa.online.rematchOffered }
                  : rematch.kind === "offer" && startRefused
                    ? { againHint: startRefused }
                    : {}),
          })
        : null}
      {/* The turn timer is rendered ON the felt by the table (threaded via `timer`). */}
      {/* CARD games: the pause overlay (host may resume with a bot). Chess never renders this. */}
      {paused && !usesAwayOverlay ? (
        <PausedOverlay paused={paused} isHost={isHost} onResume={() => store.getState().resume()} />
      ) : null}
      {/* CHESS + BACKGAMMON: the opponent-away lifecycle — reconnect countdown → (pause wait) → claim.
          `!claimable` for the same reason the pause branch below carries it: these three render the
          SAME component, and two of them at once are two identical scrims. An offered CLAIM is the
          only one of the three with a BUTTON, so it must never end up underneath a countdown — the
          shape that was measured on تخته نرد when a second announce landed on a live claim prompt.
          Unreachable today (`onResumed` clears `claimable`, and at two seats the other seat is you),
          which is exactly why it is worth being explicit rather than relying on that staying true. */}
      {/* ⚠️ NO LONGER A SCRIM (operator, 2026-09-15). شطرنج and تخته نرد used to black out the whole
          board for the entire grace; a player may want to go on studying the position while they
          wait, and a blocking overlay stops them. It is the card games' pill now — same component,
          same geometry, `pointer-events:none` — and the table's own exit is reachable again
          underneath, which is what the scrim was covering at `z-10`. */}
      {usesAwayOverlay && claimable && claimable.seat === seat && !result ? (
        <OpponentAwayOverlay
          title={awayFa.opponentLeft}
          hint={awayFa.claimHint}
          onClaim={() => store.getState().claim()}
        />
      ) : null}
      {/* ⚠️ ONE POSITIONER FOR EVERY TOP-OF-SCREEN NOTICE, BELOW THE HEADER BAND (2026-09-15).
          Each of these used to pin itself — the free-exit line below the band, the two pills INSIDE
          it — and measured on the deployed staging build with the widest name prod holds, the
          reconnect pill covered «خروج» and «گفتگو» at all four widths. They also co-occur (a
          bot-filled public table has the free-exit line up for the whole match, so a drop puts a
          pill beside it and the takeover adds the toast), so giving each the same `top`
          independently would only have traded one collision for another. `TopNoticeStack` stacks
          them in a column and renders nothing when they are all null. */}
      <TopNoticeStack>
        {leaving && gameId === "chess" ? <LeavingNotice /> : null}
        {/* ⚠️ ONE pill at a time for a single absence. شطرنج/تخته نرد/دوز freeze the table AT the
            drop, so `reconnecting` and `paused` are both live for the whole wait and both branches
            render the SAME component. The reconnecting one wins because it is the only one carrying
            the countdown the notice promises; the second is the wait once that deadline has passed
            (or a pause with no grace at all). */}
        {usesAwayOverlay && reconnecting && !claimable && !result ? (
          <ReconnectingToast name={seatName(lobby?.players, reconnecting.seat)} />
        ) : null}
        {usesAwayOverlay && paused && !claimable && !reconnecting && !result ? (
          <ReconnectingToast name={seatName(lobby?.players, paused.seat)} />
        ) : null}
        {/* منچ, during the grace: the seat is auto-played and play continues, so the others are
            never frozen and this pill is the whole of what they see. ⚠️ NOT the card tables any
            more — they say it on the seat (`reconnectingSeat`, above), because this slot is their
            felt; see `seatCarriesReconnect`. */}
        {!usesAwayOverlay && !seatCarriesReconnect && reconnecting && !result ? (
          <ReconnectingToast name={seatName(lobby?.players, reconnecting.seat)} />
        ) : null}
        {/* ⚠️ THE TWO-SEAT BOARD GAMES' DISCONNECT FLOW ONLY, FOR TEN SECONDS (2026-09-15).
            It used to be gated on `lastHumanStanding` alone and stand for as long as that was true.
            That predicate is the EXIT DIALOG's, and its `fullAtDeal === false` clause is true for
            the whole match on every table that dealt short-handed — so every bot-filled حکم table
            (and شلم, بی‌دل, پاسور, بلوف, منچ, and a private board game against a bot) showed
            «your opponent was absent for 60 seconds» from the deal to the end, on the felt's top
            row over the trump, with nothing that could dismiss it. Reproduced on the deployed
            bundle, `probe-card-notice.cjs`.
            So it needs all three: a board game for two; the grace-end takeover EVENT (the only
            moment the sentence is true — `freeExitNotice`, cleared by the store after
            `FREE_EXIT_NOTICE_MS`; at two seats it can only be about the opponent's chair, because
            a player whose own grace ran out was not connected to receive it); and
            `lastHumanStanding` still true, so it can never promise a free exit the dialog itself
            would not (an opponent who reclaims inside the ten seconds takes it down at once; and it
            is mid-game only, which is also why there is no `!result` here — `onGameOver` sets the
            result and leaves `playing` in one update).
            ⚠️ It says nothing about what the absence cost the ABSENT player, and must not: telling
            people a disconnect is free for the one who vanished teaches them to close the app. */}
        {usesAwayOverlay && freeExitNotice !== null && lastHumanStanding ? (
          <FreeExitNotice />
        ) : null}
        {/* ⚠️ NOT ON A CARD TABLE ANY MORE, EXCEPT FOR THE CROWN (2026-09-15). Five seconds of this
            sentence sat on the same felt row the reconnect pill was moved off — so the departure is
            said on the chair it happened to (`seatNotices`), and the toast stays only where it is
            about the READER: the private host left and this player inherited the table. */}
        {leftNotice && !leftGoesToSeat ? <LeftToast notice={leftNotice} isHost={isHost} /> : null}
      </TopNoticeStack>
      {confirmDialog}
    </CardDeckContext.Provider>
  );
}

/**
 * WHY THE OFFERED SUCCESSOR CANNOT BE FOLLOWED — the same sentences an invite into that table
 * would have got (`tableJoinReason`), said on the end card instead of an error screen.
 */
function successorRefusalLine(state: TableStatusResponse["state"]): string {
  return tableFateLine(state) ?? fa.invite.roomGone;
}
